import { apiUrl } from "../api/endpoints";
import React, { useEffect, useState, useContext, Fragment, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { STATIC_ICONS, defaultIcon } from '../Constants';
import CloudUpload from '@mui/icons-material/CloudUploadOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import BookmarkAddedOutlinedIcon from '@mui/icons-material/BookmarkAddedOutlined';
import CollapseContext from "../hooks/CollapseContext";
import useWindowDimensions from "../hooks/useWindowDimensions";
import axios from 'axios';
import './details.css';
import './edit.css';
import '../Components/modal.css';
import './TemplateDetails.css';
import { useAuth } from '../hooks/AuthProvider';
import { EditDatastream, EditEvent } from './EditDatastream';
import WebDashboard from './WebDashboard';
import { Tooltip } from '@mui/material';
import Spinner from '../Components/Spinner';
import { deleteWithIcon } from '../lib/actions';
import Select from 'react-select';
import DataGridTable from "../Components/DataGridTable";
import { GridActionsCellItem } from '@mui/x-data-grid';
import ToggleOff from '@mui/icons-material/ToggleOff';
import ToggleOn from '@mui/icons-material/ToggleOn';

import { LanguageContext } from '../context/LanguageContext';
import templateTranslations from '../languages/template.json';
import webDashboardTranslations from '../languages/webDashboard.json';

import Alert from '../Components/Alert';
import AppDialog from '../Components/AppDialog';
import WebDashboardLayoutModeField from '../Components/WebDashboardLayoutModeField';
import { getErrorMessage } from '../utils/errorUtils';
import {
    DATASTREAM_MIN_MAX_ABS_LIMIT,
    normalizeTemplateDatastreamCollection,
    validateDatastreamCollection,
} from '../utils/datastreamValidation';
import {
    formatDatastreamIdForDisplay,
    getNextTemporaryDatastreamId,
} from '../utils/datastreamIds';
import { DownloadTemplateReport } from './DownloadTemplateReport';

import { AddDevice } from './AddDevice';

import { EditTemplateEvaluations } from '../Components/EditTemplateEvaluations';
import {TemplateIcon, DescriptionIcon, SettingsIcon, GradientDivider, CodeIcon, CreateDataStreamIcon, ExpandMenuIcon, MenuBackButtonIcon, DatastreamsIcon, WebDashboardIcon, EventNotificationIcon, HomeIcon, DividerMenuIcon, DividerMenuOpenedIcon } from '../svg/TemplateIcons';
import { DetailsMainIcon,
    VerticalDividerIcon,
    DeleteIcon,
    DownloadReprtIcon,
    DuplicateIcon,
    CustomSwitch,
 } from '../svg/UtilsIcons';
import {
    createDefaultWebDashboardTab,
    getEffectiveFixedWidthValue,
    normalizeWebDashboardLayoutMode,
    normalizeWebDashboardTabs,
    WEB_DASHBOARD_LAYOUT_MODES,
} from '../utils/webDashboardLayout';

const getTabPopupPosition = (rect, { width = 340, estimatedHeight = 600 } = {}) => {
    if (!rect || typeof window === 'undefined') {
        return { top: 12, left: width / 2 + 12 };
    }

    const margin = 12;
    const preferredTop = rect.bottom + 8;
    const maxTop = Math.max(margin, window.innerHeight - margin - estimatedHeight);
    const minLeft = margin + width / 2;
    const maxLeft = Math.max(minLeft, window.innerWidth - margin - width / 2);
    const preferredLeft = rect.left + rect.width / 2;

    return {
        top: Math.max(margin, Math.min(preferredTop, maxTop)),
        left: Math.max(minLeft, Math.min(preferredLeft, maxLeft)),
    };
};

const hardwareOptions = [
    'Arduino', 'BBC Micro:bit', 'ESP32', 'ESP8266', 'Lantronix Fox 3', 'Microduino', 'Onion Omega',
    'Particle', 'Pycom WiPy', 'Raspberry Pi', 'Seeed Wio Terminal', 'Teensy', 'TinyDuino', 'Other'
];
const connectionTypeOptions = ['Ethernet', 'WiFi', 'Satellite', 'GSM'];


const TID_PREFIX_MAIN = 'TMPL';
const TID_PREFIX_DASH = 'TMPL-';
const normalizeExampleNameValue = (value) => String(value ?? '').trim().toLowerCase();
const sortTemplateExamples = (examples) =>
    [...(Array.isArray(examples) ? examples : [])].sort((left, right) =>
        String(left?.example_name || '').localeCompare(String(right?.example_name || ''), undefined, {
            sensitivity: 'base',
        })
    );

const parseTidParts = (tid) => {
    const raw = String(tid ?? '').replace(/\s+/g, '');
    if (!raw) return { prefix: TID_PREFIX_MAIN, suffix: '' };

    const upper = raw.toUpperCase();
    if (upper.startsWith(TID_PREFIX_DASH)) {
        return { prefix: TID_PREFIX_DASH, suffix: raw.slice(TID_PREFIX_DASH.length) };
    }
    if (upper.startsWith(TID_PREFIX_MAIN)) {
        return { prefix: TID_PREFIX_MAIN, suffix: raw.slice(TID_PREFIX_MAIN.length) };
    }
    return { prefix: TID_PREFIX_MAIN, suffix: raw };
};

const stripTidPrefix = (value) => {
    const raw = String(value ?? '').replace(/\s+/g, '');
    if (!raw) return '';

    const upper = raw.toUpperCase();
    if (upper.startsWith(TID_PREFIX_DASH)) return raw.slice(TID_PREFIX_DASH.length);
    if (upper.startsWith(TID_PREFIX_MAIN)) return raw.slice(TID_PREFIX_MAIN.length);
    return raw;
};

const ensureTidStartsWithTMPL = (tid) => {
    const raw = String(tid ?? '').replace(/\s+/g, '').trim();
    if (!raw) return '';
    return raw.toUpperCase().startsWith(TID_PREFIX_MAIN) ? raw : `${TID_PREFIX_MAIN}${raw}`;
};

const TemplateSettings = ({
    state,
    setState,
    mode,
    template,
    setTemplate,
    previousIcon,
    setPreviousIcon
}) => {
    const { permissions } = useAuth();
    const canEditTemplate = permissions['edit_templates'];

    const { lang } = useContext(LanguageContext);
    const tRoot = templateTranslations[lang].TemplateDetails;
    const t = tRoot.TemplateSettings;
    const isRtl = ['ar', 'ur'].includes(lang);

    const [alertState, setAlertState] = useState({
        open: false,
        message: '',
        type: 'info',
        position: isRtl ? 'left' : 'right'
    });

    const showAlert = (type, message) => {
        setAlertState({
            open: true,
            type,
            message,
            position: isRtl ? 'left' : 'right'
        });
    };

    const selectOption = (key, value) => {
        setTemplate({ ...template, [key]: value });
    };

    const saveFile = async e => {
        if (!canEditTemplate) {
            showAlert(
                'error',
                tRoot.noPermission ||
                    'You do not have permission to perform this action.'
            );
            return;
        }

        if (!e.target.files || e.target.files.length === 0) {
            return;
        }

        const file = e.target.files[0];

        const prefix = 'temp-' + (template.tid || 'template'),
            nameParts = file.name.split('.'),
            suffix = nameParts[nameParts.length - 1],
            random = Date.now().toString(),
            fileName = `${prefix}-${random}.${suffix}`;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileName', fileName);
        formData.append('type', 'icons');

        try {
            const res = await axios.post(
                apiUrl(`file/upload`),
                formData
            );

            showAlert(
                'success',
                res.data.message ||
                    'Icon uploaded successfully. Click Save changes to persist.'
            );

            setPreviousIcon(
                template.icon && template.icon.length > 0
                    ? template.icon
                    : previousIcon
            );

            setTemplate({ ...template, icon: fileName });
        } catch (err) {
            console.error('File upload failed:', err);
            const errorMessage = getErrorMessage(err);
            showAlert('error', errorMessage);

            e.target.value = null;
        }
    };

    if (mode === 'edit' && !canEditTemplate) {
        return (
            <div
                id="modal"
                style={{
                    visibility: state ? 'visible' : 'hidden',
                    transition: 'visibility .2s linear',
                    direction: isRtl ? 'rtl' : 'ltr'
                }}
                onClick={() => setState(false)}
            >
                <div
                    id="popup"
                    className="dashboard-modal edit-template-modal"
                    style={{
                        animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
                        fontSize:
                            'calc(var(--body-font-size) * var(--font-size-multiplier))'
                    }}
                    onClick={e => e.stopPropagation()}
                >
                    <div className="dashboard-header">
                        <div className="dashboard-header-icon">
                            <DetailsMainIcon />
                        </div>
                        <div className="dashboard-header-text">
                            <h2>{t.settings}</h2>
                            <p>
                                {tRoot.noPermission ||
                                    'You do not have the required permission to edit Templates.'}
                            </p>
                        </div>
                    </div>

                    <div className="modal-divider" />

                    <div className="modal-footer dashboard-footer">
                        <button
                            className="btn btn-cancel"
                            onClick={() => setState(false)}
                        >
                            {tRoot.buttonCancel || 'Close'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const hardwareSelectOptions = hardwareOptions.map(option => ({
        value: option,
        label: option
    }));
    const selectedHardwareOption = hardwareSelectOptions.find(
        option => option.value === template.hardware
    );

    const connectionTypeSelectOptions = connectionTypeOptions.map(option => ({
        value: option,
        label: option
    }));
    const selectedConnectionTypeOption = connectionTypeSelectOptions.find(
        option => option.value === template.connectionType
    );

    const selectStyles = {
        control: (provided, state) => ({
            ...provided,
            backgroundColor: 'var(--td-select-control-bg)',
            borderColor: state.isFocused
                ? 'var(--td-select-border-focus)'
                : 'var(--td-select-border)',
            boxShadow: state.isFocused
                ? '0 0 0 1px var(--td-select-focus-ring)'
                : 'none',
            '&:hover': {
                borderColor: 'var(--td-select-border-focus)'
            },
            color: 'var(--td-select-text)',
            fontSize:
                'calc(var(--body-font-size) * var(--font-size-multiplier))',
            minHeight: '44px',
            borderRadius: 8,
            cursor: 'pointer'
        }),
        singleValue: provided => ({
            ...provided,
            color: 'var(--td-select-text)',
            fontSize:
                'calc(var(--body-font-size) * var(--font-size-multiplier))'
        }),
        input: provided => ({
            ...provided,
            color: 'var(--td-select-text)',
            fontSize:
                'calc(var(--body-font-size) * var(--font-size-multiplier))'
        }),
        placeholder: provided => ({
            ...provided,
            color: 'var(--td-select-placeholder)',
            fontSize:
                'calc(var(--body-font-size) * var(--font-size-multiplier))'
        }),
        menu: provided => ({
            ...provided,
            backgroundColor: 'var(--td-select-menu-bg)',
            border: '1px solid var(--td-select-border)',
            borderRadius: 8,
            overflow: 'hidden',
            zIndex: 9999
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? 'var(--td-select-option-selected-bg)'
                : state.isFocused
                ? 'var(--td-select-option-hover)'
                : 'var(--td-select-menu-bg)',
            color: state.isSelected ? 'var(--td-select-option-selected-text)' : 'var(--td-select-text)',
            fontSize:
                'calc(var(--body-font-size) * var(--font-size-multiplier))',
            '&:active': {
                backgroundColor: 'var(--td-select-option-active-bg)'
            },
            cursor: 'pointer'
        }),
        dropdownIndicator: provided => ({
            ...provided,
            color: 'var(--td-select-text)',
            paddingRight: 12,
            '&:hover': {
                color: 'var(--td-select-border-focus)'
            }
        }),
        clearIndicator: provided => ({
            ...provided,
            color: 'var(--td-select-text)',
            '&:hover': {
                color: 'var(--td-select-border-focus)'
            }
        }),
        indicatorSeparator: () => ({
            display: 'none'
        })
    };

    const nameLength = template.name?.length || 0;
    const descLength = template.description?.length || 0;
    const tidParts = parseTidParts(template.tid);
    const tidPrefix = tidParts.prefix;
    const tidSuffix = tidParts.suffix;
    const tidSuffixMax = Math.max(1, 50 - String(tidPrefix || '').length);

    return (
        <div
            id="modal"
            style={{
                visibility: state ? 'visible' : 'hidden',
                transition: 'visibility .2s linear',
                direction: isRtl ? 'rtl' : 'ltr'
            }}
            onClick={() => setState(false)}
        >
            <Alert
                open={alertState.open}
                close={() =>
                    setAlertState(prev => ({ ...prev, open: false }))
                }
                message={alertState.message}
                type={alertState.type}
                position={alertState.position}
            />

            <div
                id="popup"
                className="dashboard-modal edit-template-modal"
                style={{
                    animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
                    fontSize:
                        'calc(var(--body-font-size) * var(--font-size-multiplier))',
                        width: '60%',
                        marginLeft:'10%'
                }}
                onClick={e => e.stopPropagation()}
            >
                <div className="dashboard-header">
                    <div className="dashboard-header-icon">
                        <DetailsMainIcon />
                    </div>
                    <div className="dashboard-header-text">
                        <h2>{t.settings}</h2>
                        <p>
                            {mode === 'edit'
                                ? t.editModeSubtitle || 'Edit mode'
                                : t.viewModeSubtitle || 'View mode'}
                        </p>
                    </div>
                </div>

                <div className="modal-divider" />

                <div className="modal-content dashboard-content edit-layout-grid">
                    <div className="form-section settings-icon-section">
                        <div className="form-section-title">
                            <span className="form-section-title-text">
                                {t.iconLabel}
                            </span>
                        </div>

                        <div className="form-item">
                            <div className="edit-form-item settings-main-icon-wrapper">
                                {template.icon && template.icon.length > 0 ? (
                                    <Fragment>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="edit-logo-section"
                                            title=""
                                            onChange={saveFile}
                                            disabled={mode !== 'edit'}
                                        />
                                        <div className="settings-main-icon-circle">
                                            <img
                                                src={
                                                    STATIC_ICONS + template.icon
                                                }
                                                alt=""
                                            />
                                            {mode === 'edit' && (
                                                <button
                                                    type="button"
                                                    className="settings-main-icon-remove"
                                                    onClick={() => {
                                                        setPreviousIcon(
                                                            template.icon
                                                        );
                                                        setTemplate({
                                                            ...template,
                                                            icon: ''
                                                        });
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </button>
                                            )}
                                        </div>
                                    </Fragment>
                                ) : (
                                    <Fragment>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="edit-logo-section"
                                            title=""
                                            onChange={saveFile}
                                            disabled={mode !== 'edit'}
                                        />
                                        <div className="settings-main-icon-circle settings-main-icon-empty">
                                            <CloudUpload className="td-upload-icon" />
                                        </div>
                                        <div
                                            style={{
                                                marginTop: 12,
                                                textAlign: 'center',
                                                pointerEvents: 'none',
                                                fontWeight: 500
                                            }}
                                        >
                                            {t.uploadLogo}
                                        </div>
                                        <div
                                            className="edit-helper"
                                            style={{
                                                marginTop: 4,
                                                textAlign: 'center',
                                                pointerEvents: 'none'
                                            }}
                                        >
                                            {t.uploadHelper}
                                        </div>
                                    </Fragment>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <div className="form-section">
                            <div className="form-row two-cols">
                                <div className="form-item">
                                    <div className="form-section-title">
                                        <span className="form-section-title-text">
                                            {t.nameLabel}
                                        </span>
                                    </div>
                                    <div className="field-with-counter">
                                        {mode === 'edit' ? (
                                            <input
                                                type="text"
                                               
                                                maxLength={50}
                                                value={template.name || ''}
                                                onChange={e =>
                                                    setTemplate({
                                                        ...template,
                                                        name: e.target.value
                                                    })
                                                }
                                                className="add-column td-input-sm"
                                            />
                                        ) : (
                                            <div className="modal-info field-readonly">
                                                <span
                                                    style={{
                                                        color: template.name
                                                            ? ''
                                                            : 'var(--modal-border)'
                                                    }}
                                                >
                                                    {template.name ||
                                                        tRoot.column.name}
                                                </span>
                                            </div>
                                        )}

                                        <span className="field-counter">
                                            {nameLength} / 50
                                        </span>
                                    </div>
                                </div>

                                <div className="form-item">
                                    <div className="form-section-title">
                                        <span className="form-section-title-text">
                                            {t.templateIdLabel ||
                                                'Template ID'}
                                        </span>
                                    </div>
                                    {mode === 'edit' ? (
                                        <div
                                            className="add-column td-input-sm"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 10,
                                                padding: '0 12px'
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontWeight: 700,
                                                    opacity: 0.85,
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {tidPrefix}
                                            </span>

                                            <input
                                                type="text"
                                                maxLength={tidSuffixMax}
                                                value={tidSuffix}
                                                onChange={e => {
                                                    const value = stripTidPrefix(e.target.value);
                                                    if (!value.includes(' ')) {
                                                        setTemplate({
                                                            ...template,
                                                            tid: value
                                                                ? `${tidPrefix}${value}`
                                                                : ''
                                                        });
                                                    }
                                                }}
                                                className="add-column"
                                                style={{
                                                    border: 'none',
                                                    outline: 'none',
                                                    background: 'transparent',
                                                    padding: 0,
                                                    flex: 1,
                                                    minWidth: 0
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === ' ') e.preventDefault();
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <div className="modal-info field-readonly">
                                            <span
                                                style={{
                                                    color: template.tid
                                                        ? ''
                                                        : 'var(--modal-border)'
                                                }}
                                            >
                                                {template.tid || '-'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="form-section">
                            <div className="form-section-title">
                                <span className="form-section-title-text">
                                    {t.hardwareLabel}
                                </span>
                            </div>
                            <div className="form-item">
                                {mode === 'edit' ? (
                                    <Select
                                        id="hardware"
                                        options={hardwareSelectOptions}
                                        value={selectedHardwareOption}
                                        onChange={selectedOption =>
                                            selectOption(
                                                'hardware',
                                                selectedOption
                                                    ? selectedOption.value
                                                    : null
                                            )
                                        }
                                        classNamePrefix="react-select"
                                        styles={selectStyles}
                                    />
                                ) : (
                                    <div className="modal-info field-readonly">
                                        <span
                                            style={{
                                                color: template.hardware
                                                    ? ''
                                                    : 'var(--modal-border)'
                                            }}
                                        >
                                            {template.hardware || 'Hardware'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="form-section">
                            <div className="form-section-title">
                                <span className="form-section-title-text">
                                    {t.connectionTypeLabel}
                                </span>
                            </div>
                            <div className="form-item">
                                {mode === 'edit' ? (
                                    <Select
                                        id="connectionType"
                                        options={connectionTypeSelectOptions}
                                        value={selectedConnectionTypeOption}
                                        onChange={selectedOption =>
                                            selectOption(
                                                'connectionType',
                                                selectedOption
                                                    ? selectedOption.value
                                                    : null
                                            )
                                        }
                                        classNamePrefix="react-select"
                                        styles={selectStyles}
                                    />
                                ) : (
                                    <div className="modal-info field-readonly">
                                        <span
                                            style={{
                                                color: template.connectionType
                                                    ? ''
                                                    : 'var(--modal-border)'
                                            }}
                                        >
                                            {template.connectionType ||
                                                t.connectionTypeLabel}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="form-section">
                            <div className="form-section-title">
                                <span className="form-section-title-text">
                                    {t.descriptionoptionalLabel}
                                </span>
                            </div>
                            <div className="form-item">
                                <div className="field-with-counter field-with-counter--textarea">
                                    {mode === 'edit' ? (
                                        <textarea
                                            value={template.description || ''}
                                            onChange={e =>
                                                setTemplate({
                                                    ...template,
                                                    description: e.target.value
                                                })
                                            }
                                            className="add-column textarea-large td-textarea"
                                            maxLength={128}
                                            rows={5}
                                            placeholder={t.descriptionLabel}
                                        />
                                    ) : (
                                        <div
                                            className="modal-info field-readonly"
                                            style={{
                                                height: 160,
                                                alignItems: 'flex-start'
                                            }}
                                        >
                                            <span
                                                style={{
                                                    color: template.description
                                                        ? ''
                                                        : 'var(--modal-border)'
                                                }}
                                            >
                                                {template.description ||
                                                    t.descriptionLabel}
                                            </span>
                                        </div>
                                    )}
                                    <span className="field-counter">
                                        {descLength} / 128
                                    </span>
                                </div>
                            </div>
                        </div>
                                            <div className="form-section" style={{ width: '100%' }}>
                        <div className="form-section-title">
                            <span className="form-section-title-text">
                                {t.hotspotPrefixLabel}
                            </span>
                        </div>
                        <div className="form-item">
                            {mode === 'edit' ? (
                                <input
                                    type="text"
                                    value={template.hotspotPrefix || ''}
                                    onChange={e =>
                                        setTemplate({
                                            ...template,
                                            hotspotPrefix: e.target.value
                                        })
                                    }
                                   
                                    maxLength={50}
                                    className="add-column"
                                />
                            ) : (
                                <div className="modal-info field-readonly">
                                    <span
                                        style={{
                                            color: template.hotspotPrefix
                                                ? ''
                                                : 'var(--modal-border)'
                                        }}
                                    >
                                        {template.hotspotPrefix ||
                                            t.hotspotPrefixLabel}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                    </div>
                </div>

                <div className="modal-divider" />


                <div className="modal-footer dashboard-footer">
                    <button
                        className="btn btn-cancel"
                        onClick={() => setState(false)}
                    >
                        {tRoot.buttonCancel || 'Cancel'}
                    </button>
                    <button
                        className="btn btn-apply dashboard-primary-btn"
                        onClick={() => setState(false)}
                    >
                        {tRoot.saveChanges || 'Save changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const copyToClipboard = (value) => {
    if (!value) return;
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).catch(() => {});
    }
};

const TemplateHomeCard = ({ title, subtitle, right, children }) => (
    <div className="td-card" style={{ overflow:'auto' }}>
        <div className="td-card__header">
            <div className="td-card__header-left">
                <div className="td-card__title-row">
                    <h5 className="td-card__title">{title}</h5>
                    {subtitle ? (
                        <span className="td-card__subtitle">{subtitle}</span>
                    ) : null}
                </div>
            </div>
            {right}
        </div>

        <div className="td-card__spacer" />
        <div className="td-card__body">{children}</div>
    </div>
);
const TemplateHomeField = ({ label, value, action, multiline }) => (
    <section className="td-field">
        <div className="td-field__top">
            <span className="td-field__label">{label}</span>
            {action}
        </div>

        <div className={multiline ? "td-field__box td-field__box--multiline" : "td-field__box"}>
            <div
                className={
                    multiline
                        ? (value ? "td-field__value td-field__value--multiline" : "td-field__value td-field__value--multiline td-field__value--empty")
                        : (value ? "td-field__value" : "td-field__value td-field__value--empty")
                }
            >
                {value || '-'}
            </div>
        </div>
    </section>
);
const TemplateHomeSettingsPanel = ({
    t,
    template,
    edit,
    canEditTemplate,
    setField,
}) => {
    return (
        <TemplateHomeCard
            title={t.exampleSettingsTitle || 'Template Example Settings'}
            subtitle={template.example_name || ''}
            right={null}
        >
            {edit.mode === 'edit' && canEditTemplate ? (
                <div className="td-edit-grid">
                    <div>
                        <div className="td-edit-label">
                            {t.TemplateSettings?.nameLabel || 'Name'}
                        </div>
                        <input
                            type="text"
                            maxLength={100}
                            value={template.name || ''}
                            onChange={(e) => setField('name', e.target.value)}
                            className="add-column td-input"
                        />
                    </div>

                    <div>
                        <div className="td-edit-label">
                            {t.exampleNameLabel || 'Example Name'}
                        </div>
                        <input
                            type="text"
                            maxLength={100}
                            value={template.example_name || ''}
                            onChange={(e) => setField('example_name', e.target.value)}
                            className="add-column td-input"
                        />
                    </div>
                </div>
            ) : (
                <div>
                    <TemplateHomeField
                        label={t.TemplateSettings?.nameLabel || 'Name'}
                        value={template.name || '-'}
                        action={
                            template.name ? (
                                <button
                                    type="button"
                                    onClick={() => copyToClipboard(template.name)}
                                    className="td-circle-btn"
                                    title="Copy"
                                >
                                    <CodeIcon />
                                </button>
                            ) : null
                        }
                    />
                    <TemplateHomeField
                        label={t.exampleNameLabel || 'Example Name'}
                        value={template.example_name || '-'}
                        action={
                            template.example_name ? (
                                <button
                                    type="button"
                                    onClick={() => copyToClipboard(template.example_name)}
                                    className="td-circle-btn"
                                    title="Copy"
                                >
                                    <CodeIcon />
                                </button>
                            ) : null
                        }
                    />
                </div>
            )}
        </TemplateHomeCard>
    );
};

const TemplateHomeOverviewPanel = ({
    t,
    details,
}) => {
    const datastreamCount = Array.isArray(details?.datastreams)
        ? details.datastreams.filter(Boolean).length
        : 0;
    const webdashboardCount = Array.isArray(details?.webdashboard)
        ? details.webdashboard.filter(Boolean).length
        : 0;
    const mobdashboardCount = Array.isArray(details?.mobdashboard)
        ? details.mobdashboard.filter(Boolean).length
        : 0;
    const eventsCount = Array.isArray(details?.events)
        ? details.events.filter(Boolean).length
        : 0;

    return (
        <TemplateHomeCard
            title={t.homeHeader || 'Home'}
            subtitle={t.exampleOverviewSubtitle || 'Current content'}
        >
            <div>
                <TemplateHomeField
                    label={t.datastreamsHeader || 'Datastreams'}
                    value={String(datastreamCount)}
                />
                <TemplateHomeField
                    label={t.tabs?.webdashboard || 'Web Dashboard'}
                    value={String(webdashboardCount)}
                />
                <TemplateHomeField
                    label={t.mobileDashboardLabel || 'Mobile Dashboard'}
                    value={String(mobdashboardCount)}
                />
                <TemplateHomeField
                    label={t.eventsHeader || 'Events'}
                    value={String(eventsCount)}
                />
            </div>
        </TemplateHomeCard>
    );
};

const TemplateHomeTab = ({
    details,
    setDetails,
    edit,
    setEdit,
    canEditTemplate,
    canAddTemplate,
    canProvisionDevices,
    canDeleteTemplate,
    canDownloadReports,
    settings,
    setSettings,
    previousIcon,
    setPreviousIcon,
    downloadReportState,
    setDownloadReportState,
    t,
    isRtl,
    navigate,
    updateTemplate,
    handleCancelEdit,
    onOpenAddDevice
}) => {
    if (!details) return null;

    const template = details;

    const setField = (key, value) => setDetails({ ...template, [key]: value });

    return (
        <div className="asset-card-page td-home-page">
            <div className="td-home-grid">
                <TemplateHomeOverviewPanel
                    t={t}
                    details={details}
                />

                <TemplateHomeSettingsPanel
                    t={t}
                    template={template}
                    edit={edit}
                    canEditTemplate={canEditTemplate}
                    setField={setField}
                />
            </div>
        </div>
    );
};


const TemplateDatastreamsTab = ({
    details,
    setDetails,
    edit,
    setEdit,
    addDatastream,
    setAddDatastream,
    templateEvaluationData,
    setTemplateEvaluationData,
    isEvalModalOpen,
    setIsEvalModalOpen,
    canEditTemplate,
    canEditDatastream,
    canDeleteDatastream,
    t,
    pagingoptions,
    colors,
    handleRowClick,
    selectedRows,
    setSelectedRows,
    selectActions,
    pagination,
    size,
    pinCategories,
    fields
}) => {
    const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);

    const rows = details.datastreams?.filter(ds => ds) ?? [];

    const FakeIcon = () => (<span className="td-fake-icon" />);

    const handleNewDatastreamCategory = cat => {
        setIsCategoriesOpen(false);
        setAddDatastream({
            edit: true,
            addEvents: false,
            pinCategory: cat,
            isNew: true
        });
    };

    const getNextDatastreamIdForTab = list => {
        return getNextTemporaryDatastreamId(list);
    };

    const getNextAvailableVirtualPinForTab = usedPins => {
        for (let i = 0; i < 500; i++) {
            const p = `V${i}`;
            if (!usedPins.has(p)) return p;
        }
        return '';
    };

    const duplicateSelectedDatastreams = useCallback((sourceIds) => {
        const normalizedIds = Array.from(
            new Set((Array.isArray(sourceIds) ? sourceIds : [sourceIds])
                .map(id => Number(id))
                .filter(id => Number.isFinite(id)))
        );

        if (normalizedIds.length === 0) return;

        const existing = (details.datastreams || []).filter(Boolean);
        const sourceMap = new Map(existing.map(ds => [Number(ds.id), ds]));
        const sources = normalizedIds
            .map(id => sourceMap.get(id))
            .filter(Boolean);

        if (sources.length === 0) return;

        const usedPins = new Set(existing.map(d => d.pin).filter(Boolean));
        const prevRanges = templateEvaluationData?.datastream_ranges || [];
        const clonedDatastreams = [];
        const clonedRanges = [];
        let nextId = getNextDatastreamIdForTab(existing);

        sources.forEach(source => {
            let newPin = source.pin;
            if (!newPin || usedPins.has(newPin)) {
                newPin = getNextAvailableVirtualPinForTab(usedPins);
            }
            if (newPin) {
                usedPins.add(newPin);
            }

            const newId = nextId++;
            clonedDatastreams.push({
                ...source,
                id: newId,
                pin: newPin,
            });

            prevRanges
                .filter(r => Number(r.datastream_id) === Number(source.id))
                .forEach(r => {
                    clonedRanges.push({
                        ...r,
                        datastream_evaluation_id: null,
                        datastream_id: newId,
                    });
                });
        });

        if (clonedDatastreams.length === 0) return;

        setDetails(prev => ({
            ...prev,
            datastreams: [...(prev?.datastreams || []).filter(Boolean), ...clonedDatastreams],
        }));

        if (clonedRanges.length > 0) {
            setTemplateEvaluationData(prevData => ({
                ...prevData,
                datastream_ranges: [...(prevData?.datastream_ranges || []), ...clonedRanges],
            }));
        }

        setSelectedRows([]);
    }, [details.datastreams, setDetails, setSelectedRows, setTemplateEvaluationData, templateEvaluationData?.datastream_ranges]);

    const handleDeleteSelected = useCallback(() => {
        if (!canDeleteDatastream) return;
        if (selectedRows.length === 0) return;

        if (!window.confirm(`${t.confirmDeleteSelected || 'Are you sure you want to delete'} ${selectedRows.length} ${t.selectedDatastreams || 'selected datastream(s)'}?`)) {
            return;
        }

        const datastreamIdsToDelete = new Set(
            selectedRows
                .map(id => Number(id))
                .filter(id => Number.isFinite(id))
        );

        setDetails(prev => ({
            ...prev,
            datastreams: prev.datastreams.filter(ds => !datastreamIdsToDelete.has(ds.id))
        }));

        setTemplateEvaluationData(prevData => ({
            ...prevData,
            datastream_ranges: (prevData?.datastream_ranges || []).filter(
                r => !datastreamIdsToDelete.has(r.datastream_id)
            )
        }));

        setSelectedRows([]); 
    }, [canDeleteDatastream, selectedRows, setDetails, setSelectedRows, setTemplateEvaluationData, t]);

    const toolbarSelectActions = useMemo(() => {
        const duplicateAction = {
            icon: <DuplicateIcon style={{ color: 'var(--accent)' }} />,
            title: t.duplicateDatastream || t.duplicate || 'Duplicate Selected',
            action: duplicateSelectedDatastreams,
        };

        const deleteAction = {
            icon: <DeleteIcon style={{ color: 'var(--red-color, rgb(236, 69, 78))' }} />, 
            title: t.deleteSelected || 'Delete Selected',
            action: handleDeleteSelected,
        };

        return [
            ...(canEditDatastream ? [duplicateAction] : []),
            ...(canDeleteDatastream ? [deleteAction] : []),
        ];
    }, [canDeleteDatastream, canEditDatastream, t, handleDeleteSelected, duplicateSelectedDatastreams]);
 
    return (
        <Fragment>
            <div className="td-panel">
                <div className="td-panel__top">
                    <h5 className="td-panel__title">
                        {t.datastreamsHeader}
                    </h5>
                </div>

                {edit.mode === 'edit' && canEditTemplate && (
                    <div className="td-panel__actions">
                        <div className="td-menu-wrapper">
                            <button type="button" onClick={() => setIsCategoriesOpen(prev => !prev)} className="td-primary-btn td-primary-btn--compact">
                                <CreateDataStreamIcon />
                                <span>{t.newDatastream}</span>
                            </button>

                            {isCategoriesOpen && (
                                <ul className="td-category-menu">
                                    {pinCategories.map((cat, index) => (
                                        <li key={index} className="td-category-item" onClick={() => handleNewDatastreamCategory(cat)}>
                                            <FakeIcon />
                                            <span>{cat}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}

                <div
                    className="table-wrapper"
                    style={{
                        flex: 1,
                        minHeight: 0,
                        marginTop: 4
                    }}
                >
                    <DataGridTable
                        isDetail={true}
                        columns={fields}
                        rows={rows}
                        pagingoptions={pagingoptions}
                        showToolbar={true}
                        handleRowClick={handleRowClick}
                        idcolumn={'id'}
                        checkboxSelection={edit.mode === 'edit' && (canEditDatastream || canDeleteDatastream)}
                        rowSelectionModel={selectedRows}
                        setRowSelectionModel={setSelectedRows}
                        selectActions={toolbarSelectActions} 
                        pagination={pagination}
                        size={size}
                        tableKey="templateDatastream"
                    />
                </div>
            </div>
        </Fragment>
    );
};

const TemplateEventsTab = ({
    details,
    setAddDatastream,
    edit,
    canEditEvent,
    t,
    eventsFields,
    pagingoptions,
    eventRowClick,
    selectedRows,
    setSelectedRows,
    selectActions,
    pagination,
    size
}) => {
    const rows = details.events?.filter(ev => ev) ?? [];

    return (
        <Fragment>
            <div className="td-panel">
                <div className="td-panel__top">
                    <h5 className="td-panel__title">
                        {t.eventsHeader}
                    </h5>
                </div>

                {edit.mode === 'edit' && canEditEvent && (
                    <div className="td-panel__actions">
                        <button type="button" onClick={() => setAddDatastream(prev => ({ ...prev, addEvents: true }))} className="td-primary-btn td-primary-btn--compact">
                            <span>+</span>
                            <span>{t.newEvent}</span>
                        </button>
                    </div>
                )}

                <div
                    className="table-wrapper"
                    style={{ flex: 1, minHeight: 0, marginTop: 4 }}
                >
                    <DataGridTable
                        isDetail={true}
                        columns={eventsFields}
                        rows={rows}
                        pagingoptions={pagingoptions}
                        showToolbar={true}
                        handleRowClick={eventRowClick}
                        idcolumn={'id'}
                        rowSelectionModel={selectedRows}
                        setRowSelectionModel={setSelectedRows}
                        selectActions={selectActions}
                        pagination={pagination}
                        size={size}
                        tableKey="templateEvent"
                    />
                </div>
            </div>
        </Fragment>
    );
};


const pinCategories = ['Virtual Pin', 'Enum', 'Location', 'Digital Pin', 'Analog Pin'];

const checkTabPermission = (userRole, allowedRoles) => {
    const role = userRole?.toLowerCase();


    if (!Array.isArray(allowedRoles) || allowedRoles.length === 0) {
        return false;
    }

    return allowedRoles.map(r => r.toLowerCase()).includes(role);
};


export const TemplateExampleDetails = () => {
    const [initialTemplate, setInitialTemplate] = useState({});
 const deepClone = (obj) => JSON.parse(JSON.stringify(obj ?? {}));
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user, permissions } = useAuth();
    const userRole = user.role?.toLowerCase();
    const dims = useWindowDimensions();
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState(false);
    const [previousIcon, setPreviousIcon] = useState('');
    const origin = searchParams.get('origin');
    const originDeviceId = searchParams.get('deviceId');
    const [isEvalModalOpen, setIsEvalModalOpen] = useState(false); 
    const [edit, setEdit] = useState({
        edit: false,
        tempId: id,
        dataId: 0,
        mode: searchParams.get('mode') || 'view', 
        editEvents: false,
        eventId: 0
    });
    const [tabsWidth, setTabsWidth] = useState(false);
    const [addDatastream, setAddDatastream] = useState({
        edit: false,
        pinCategory: '',
        addEvents: false,
        isNew: false
    });
    const [downloadReportState, setDownloadReportState] = useState({
        open: false,
        template: null
    });
    const [addDevice, setAddDevice] = useState(false);
    const [isHeaderActionsMenuOpen, setIsHeaderActionsMenuOpen] = useState(false);
    const [isMobileDetailsRailOpen, setIsMobileDetailsRailOpen] = useState(false);
    const [isCurrentDashboardFixedWidth, setIsCurrentDashboardFixedWidth] = useState(true);
    const [isFixedWidthToggleReady, setIsFixedWidthToggleReady] = useState(false);
    const [isMultiSelectEnabled, setIsMultiSelectEnabled] = useState(false);
    const [isMultiSelectToggleReady, setIsMultiSelectToggleReady] = useState(false);
    const [dashboardViewportHeight, setDashboardViewportHeight] = useState(null);
    const fixedWidthToggleHandlerRef = useRef(null);
    const multiSelectToggleHandlerRef = useRef(null);
    const dashboardBodyRef = useRef(null);
    const dashboardPanelRef = useRef(null);
    const dashboardViewportRef = useRef(null);
    const [details, setDetails] = useState(null);
    const [templateExamples, setTemplateExamples] = useState([]);
    const [hasLoadedTemplateExamples, setHasLoadedTemplateExamples] = useState(false);
    const [loadingTemplateExamples, setLoadingTemplateExamples] = useState(false);
    const [markAsExampleDialog, setMarkAsExampleDialog] = useState({
        open: false,
        exampleName: '',
        error: '',
        submitting: false,
    });
    const headerActionsMenuRef = useRef(null);
    
    const [templateEvaluationData, setTemplateEvaluationData] = useState({ 
        evaluation_names: [], 
        datastream_ranges: [] 
    });


    const { lang } = useContext(LanguageContext);
    const t = templateTranslations[lang].TemplateDetails;
    const webDashboardT = webDashboardTranslations[lang]?.webDashboard || {};
    const isRtl = ['ar', 'ur'].includes(lang);
    const isCompactHeaderActions = dims.width <= 1280;
    const isMobileTemplateDetailsLayout = dims.width <= 768;

    const [alertState, setAlertState] = useState({
        open: false,
        message: '',
        type: 'info',
        position: isRtl ? 'left' : 'right',
    });

    const showAlert = useCallback((type, message) => {
        setAlertState({ open: true, type, message, position: isRtl ? 'left' : 'right' });
    }, [setAlertState, isRtl]);

    const loadTemplateExamples = useCallback(async () => {
        if (loadingTemplateExamples) {
            return templateExamples;
        }

        setLoadingTemplateExamples(true);
        try {
            const res = await axios.get(apiUrl(`template/examples/list`));
            if (res.data?.status !== 'OK' || !Array.isArray(res.data.data)) {
                throw new Error('Invalid template examples response.');
            }

            const sortedExamples = sortTemplateExamples(res.data.data);
            setTemplateExamples(sortedExamples);
            setHasLoadedTemplateExamples(true);
            return sortedExamples;
        } catch (err) {
            console.error('[TemplateDetails] Failed to load template examples:', err);
            showAlert('error', t.markAsExampleLoadError || 'Failed to load template examples.');
            return [];
        } finally {
            setLoadingTemplateExamples(false);
        }
    }, [loadingTemplateExamples, showAlert, t.markAsExampleLoadError, templateExamples]);

    const resolverRef = useRef(null);
    const [dialog, setDialog] = useState({
        open: false,
        type: 'alert', // 'alert' | 'confirm'
        variant: 'info', // 'info' | 'danger' | 'error'
        icon: 'alert', // 'alert' | 'delete'
        title: '',
        message: '',
        confirmText: t.buttonOk || templateTranslations[lang].buttonOk || 'OK',
        cancelText: t.buttonCancel || templateTranslations[lang].buttonCancel || 'Cancel',
        showCancel: false,
    });

    const closeDialog = useCallback((result = false) => {
        setDialog(d => ({ ...d, open: false }));
        if (resolverRef.current) {
            resolverRef.current(result);
            resolverRef.current = null;
        }
    }, []);

    const alert = useCallback(
        (opts) =>
            new Promise(resolve => {
                resolverRef.current = resolve;

                setDialog({
                    open: true,
                    type: opts?.type || 'alert',
                    variant: opts?.variant || 'info',
                    icon: opts?.icon || 'alert',
                    title: opts?.title || '',
                    message: opts?.message || '',
                    confirmText: opts?.confirmText || t.buttonOk || templateTranslations[lang].buttonOk || 'OK',
                    cancelText: opts?.cancelText || t.buttonCancel || templateTranslations[lang].buttonCancel || 'Cancel',
                    showCancel: Boolean(opts?.showCancel),
                });
            }),
        [lang, t.buttonCancel, t.buttonOk],
    );

    const [allRoles, setAllRoles] = useState([]);
    
    const fetchRoles = useCallback(async () => {
        try {
            const res = await axios.get(apiUrl(`roles`));
            const roleNames = res.data.data.map(role => role.name.toLowerCase()); 
            setAllRoles(roleNames);
        } catch (err) {
            console.error("Failed to fetch roles:", err);
            showAlert('error', t.failedToLoadRoles || 'Failed to load roles for access control.');
            setAllRoles(['user', 'staff', 'owner', 'super']); 
        }
    }, [showAlert, setAllRoles]);
    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    useEffect(() => {
        if (!isCompactHeaderActions) {
            setIsHeaderActionsMenuOpen(false);
        }
    }, [isCompactHeaderActions]);

    useEffect(() => {
        if (!isMobileTemplateDetailsLayout) {
            setIsMobileDetailsRailOpen(false);
        }
    }, [isMobileTemplateDetailsLayout]);

    useEffect(() => {
        if (!isHeaderActionsMenuOpen) return undefined;

        const handlePointerDown = (event) => {
            if (headerActionsMenuRef.current?.contains(event.target)) {
                return;
            }

            setIsHeaderActionsMenuOpen(false);
        };

        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [isHeaderActionsMenuOpen]);

    const isDetailsRailExpanded = isMobileTemplateDetailsLayout || tabsWidth;


    const canAddTemplateExample = permissions['add_template_examples'];
    const canEditTemplateExample = permissions['edit_template_examples'];
    const canDeleteTemplateExample = permissions['delete_template_examples'];
    const canViewTemplateExample = permissions['view_template_examples'];
    const canEditTemplate = canEditTemplateExample;
    const canDeleteTemplate = canDeleteTemplateExample;
    const canAddTemplate = canAddTemplateExample;
    const canProvisionDevices = false;
    const canViewTemplate = canViewTemplateExample;
    const canViewEvents = canViewTemplate;
    const canEditEvent = canEditTemplateExample;
    const canDeleteEvent = canDeleteTemplateExample;
    const canViewDatastreams = canViewTemplate;
    const canEditDatastream = canEditTemplateExample;
    const canDeleteDatastream = canDeleteTemplateExample;
    const canDownloadReports = false;
    const canAddTemplateExamples = false;

    const normalizedExistingExampleNames = useMemo(
        () =>
            new Set(
                templateExamples
                    .map((example) => normalizeExampleNameValue(example?.example_name))
                    .filter(Boolean)
            ),
        [templateExamples]
    );

    const normalizedDialogExampleName = useMemo(
        () => normalizeExampleNameValue(markAsExampleDialog.exampleName),
        [markAsExampleDialog.exampleName]
    );

    const isExampleNameDuplicate = useMemo(
        () =>
            Boolean(
                normalizedDialogExampleName &&
                    hasLoadedTemplateExamples &&
                    normalizedExistingExampleNames.has(normalizedDialogExampleName)
            ),
        [hasLoadedTemplateExamples, normalizedDialogExampleName, normalizedExistingExampleNames]
    );

    const openMarkAsExampleDialog = useCallback(async () => {
        if (!canAddTemplateExamples) {
            showAlert('error', t.markAsExamplePermissionError || 'You do not have permission to perform this action.');
            return;
        }

        if (!details?.id) {
            showAlert('warning', t.markAsExampleTemplateMissing || 'Template details are not available yet.');
            return;
        }

        if (edit.mode === 'edit') {
            showAlert('warning', t.markAsExampleApplyFirst || 'Apply or cancel your template changes before marking it as an example.');
            return;
        }

        setMarkAsExampleDialog({
            open: true,
            exampleName: String(details?.name || '').trim(),
            error: '',
            submitting: false,
        });

        if (!hasLoadedTemplateExamples && !loadingTemplateExamples) {
            await loadTemplateExamples();
        }
    }, [
        canAddTemplateExamples,
        details,
        edit.mode,
        hasLoadedTemplateExamples,
        loadTemplateExamples,
        loadingTemplateExamples,
        showAlert,
        t.markAsExampleApplyFirst,
        t.markAsExamplePermissionError,
        t.markAsExampleTemplateMissing,
    ]);

    const closeMarkAsExampleDialog = useCallback(() => {
        setMarkAsExampleDialog((prev) =>
            prev.submitting
                ? prev
                : {
                      open: false,
                      exampleName: '',
                      error: '',
                      submitting: false,
                  }
        );
    }, []);

    const handleMarkAsExampleNameChange = useCallback((event) => {
        const nextValue = event.target.value;
        setMarkAsExampleDialog((prev) => ({
            ...prev,
            exampleName: nextValue,
            error: '',
        }));
    }, []);

    const submitMarkAsExample = useCallback(async () => {
        if (!canAddTemplateExamples) {
            showAlert('error', t.markAsExamplePermissionError || 'You do not have permission to perform this action.');
            return;
        }

        if (!details?.id) {
            showAlert('warning', t.markAsExampleTemplateMissing || 'Template details are not available yet.');
            return;
        }

        const trimmedExampleName = String(markAsExampleDialog.exampleName || '').trim();
        if (!trimmedExampleName) {
            setMarkAsExampleDialog((prev) => ({
                ...prev,
                error: t.markAsExampleNameRequired || 'Example name is required.',
            }));
            return;
        }

        if (hasLoadedTemplateExamples && normalizedExistingExampleNames.has(normalizeExampleNameValue(trimmedExampleName))) {
            setMarkAsExampleDialog((prev) => ({
                ...prev,
                error: t.markAsExampleUnique || 'Example name must be unique.',
            }));
            return;
        }

        setMarkAsExampleDialog((prev) => ({
            ...prev,
            submitting: true,
            error: '',
        }));

        try {
            const res = await axios.post(
                apiUrl(`template/examples/from-template/${details.id}`),
                { example_name: trimmedExampleName },
                { headers: { 'Content-Type': 'application/json' } }
            );

            if (res.status !== 201 || res.data?.status !== 'OK') {
                throw new Error('Received an unexpected response from the server.');
            }

            setTemplateExamples((prev) => {
                const next = prev.filter(
                    (example) =>
                        normalizeExampleNameValue(example?.example_name) !==
                        normalizeExampleNameValue(trimmedExampleName)
                );
                next.push(res.data.data);
                return sortTemplateExamples(next);
            });
            setHasLoadedTemplateExamples(true);
            setMarkAsExampleDialog({
                open: false,
                exampleName: '',
                error: '',
                submitting: false,
            });
            showAlert('success', res.data.message || (t.markAsExampleSuccess || 'Template example created successfully.'));
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            setMarkAsExampleDialog((prev) => ({
                ...prev,
                submitting: false,
                error: errorMessage,
            }));
            showAlert('error', errorMessage);
        }
    }, [
        canAddTemplateExamples,
        details,
        hasLoadedTemplateExamples,
        markAsExampleDialog.exampleName,
        normalizedExistingExampleNames,
        showAlert,
        t.markAsExampleNameRequired,
        t.markAsExamplePermissionError,
        t.markAsExampleSuccess,
        t.markAsExampleTemplateMissing,
        t.markAsExampleUnique,
    ]);

    const isMarkAsExampleSubmitDisabled =
        markAsExampleDialog.submitting ||
        !String(markAsExampleDialog.exampleName || '').trim() ||
        isExampleNameDuplicate;

    const pagingoptions = [10, 20, 50, 100];
    const colors = {
        headerBackground: 'var(--header-background)',
        headerColor: 'var(--header-color)',
        headerHover: 'var(--header-hover)',
        cellBackground: 'var(--cell-background)',
        cellColor: 'var(--cell-color)',
        cellHover: 'var(--cell-hover)',
        orderColor: 'var(--order-color)',
        orderSelectedColor: 'rgb(49, 199, 149)',
        checkboxSelection: 'var(--accent)',
        btnBackground: 'rgb(219, 245, 236)',
        btnColor: 'var(--accent)',
        modalBorder: 'var(--modal-border)',
        red: 'rgb(236, 69, 78)'
    };

    const eventsFields = [
        (edit.mode === 'edit' && canDeleteEvent) ?
            {
                field: 'actions', headerName: t.column.actions, type: 'actions', width: 100,
                getActions: (params) => [
                    <GridActionsCellItem
                        icon={<Tooltip title={t.delete}><DeleteIcon /></Tooltip>}
                        showInMenu
                        label={t.delete}
                        onClick={() => setDetails(prev => ({
                            ...prev,
                            events: prev.events?.filter(ev => ev.id !== params.row.id)
                        }))}
                    />,
                ],
            } : {
                field: 'actions',
                headerName: t.column.actions,
                width: 100,
                cellClassName: 'actions-disabled',
                renderCell: () => null
            },
        { field: 'id', headerName: t.column.id, width: 100, align: 'left', headerAlign: 'left', type: 'number' },
        { field: 'name', headerName: t.column.name, width: 200, align: 'left', headerAlign: 'left' },
        { field: 'code', headerName: t.column.code, width: 200, align: 'left', headerAlign: 'left' },
        {
            field: 'color', headerName: t.column.color, align: 'center',
            headerAlign: 'center', renderCell: (params) => {
                return (
                    <div
                        style={{
                            position: 'relative',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '25px',
                            width: '25px',

                            backgroundColor: params.row.color,

                        }}
                    >
                    </div>
                );
            },
        },
        { field: 'type', headerName: t.column.type, width: 200, align: 'left', headerAlign: 'left' },
        {
            field: 'notification', headerName: t.column.notification, width: 100, align: 'center',
            headerAlign: 'center', renderCell: (params) => {

                return (
                    params.row.notification ?
                        <ToggleOn style={{
                            color: 'var(--accent)', fontSize: 'calc(var(--icon-size) * var(--font-size-multiplier))', cursor: 'not-allowed', opacity: .5
                        }} /> :
                        <ToggleOff style={{
                            color: colors.cellColor, fontSize: 'calc(var(--icon-size) * var(--font-size-multiplier))', cursor: 'not-allowed', opacity: .5
                        }} />
                );
            },
        },
        {
            field: 'condition', headerName: t.column.condition, width: 100, align: 'center',
            headerAlign: 'center', renderCell: (params) => {

                return (
                    params.row.condition ?
                        <ToggleOn style={{
                            color: 'var(--accent)', fontSize: 'calc(var(--icon-size) * var(--font-size-multiplier))', cursor: 'not-allowed', opacity: .5
                        }} /> :
                        <ToggleOff style={{
                            color: colors.cellColor, fontSize: 'calc(var(--icon-size) * var(--font-size-multiplier))', cursor: 'not-allowed', opacity: .5
                        }} />
                );
            },
        },
        {
            field: 'action', headerName: t.column.action, width: 100, align: 'center',
            headerAlign: 'center', renderCell: (params) => {

                return (
                    params.row.action ?
                        <ToggleOn style={{
                            color: 'var(--accent)', fontSize: 'calc(var(--icon-size) * var(--font-size-multiplier))', cursor: 'not-allowed', opacity: .5
                        }} /> :
                        <ToggleOff style={{
                            color: colors.cellColor, fontSize: 'calc(var(--icon-size) * var(--font-size-multiplier))', cursor: 'not-allowed', opacity: .5
                        }} />
                );
            },
        },
        
    ];

    const getNextDatastreamId = (list) => {
        return getNextTemporaryDatastreamId(list);
    };

    const getNextAvailableVirtualPin = (usedPins) => {
        for (let i = 0; i < 500; i++) {
            const p = `V${i}`;
            if (!usedPins.has(p)) return p;
        }
        return '';
    };

    const duplicateGuardRef = useRef({ sourceId: null, ts: 0 });

    const handleDuplicateDatastream = (payloadOrId) => {
        try {
            const existing = (details.datastreams || []).filter(Boolean);

            const sourceId =
                typeof payloadOrId === 'number'
                    ? payloadOrId
                    : typeof payloadOrId === 'string'
                        ? parseInt(payloadOrId)
                        : payloadOrId?.datastream?.id;

            const source =
                (payloadOrId && payloadOrId.datastream) ||
                existing.find(ds => Number(ds.id) === Number(sourceId));

            if (!source) return;

            const now = Date.now();
            if (
                duplicateGuardRef.current.sourceId === Number(source.id) &&
                now - duplicateGuardRef.current.ts < 500
            ) {
                return;
            }
            duplicateGuardRef.current = { sourceId: Number(source.id), ts: now };

            const usedPins = new Set(existing.map(d => d.pin).filter(Boolean));

            const newId = getNextDatastreamId(existing);

            let newPin = source.pin;
            if (!newPin || usedPins.has(newPin)) {
                newPin = getNextAvailableVirtualPin(usedPins);
            }

            const clonedDatastream = {
                ...source,
                id: newId,
                pin: newPin,
            };

            setDetails(prev => {
                const current = (prev?.datastreams || []).filter(Boolean);
                if (current.some(ds => Number(ds.id) === Number(newId))) {
                    return prev;
                }
                return {
                    ...prev,
                    datastreams: [...current, clonedDatastream],
                };
            });

            const incomingEvals = payloadOrId?.evaluations;
            const prevRanges = templateEvaluationData?.datastream_ranges || [];

            const sourceRanges = Array.isArray(incomingEvals)
                ? incomingEvals
                : prevRanges.filter(r => Number(r.datastream_id) === Number(source.id));

            const clonedRanges = (sourceRanges || []).map(r => ({
                ...r,
                datastream_evaluation_id: null,
                datastream_id: newId,
            }));

            setTemplateEvaluationData(prevData => {
                const keep = (prevData?.datastream_ranges || []).filter(
                    r => Number(r.datastream_id) !== Number(newId)
                );
                return {
                    ...prevData,
                    datastream_ranges: [...keep, ...clonedRanges],
                };
            });

            setEdit(prev => ({
                ...prev,
                edit: true,
                mode: 'edit',
                dataId: newId,
            }));
        } catch (e) {
            console.error('[TemplateDetails] Duplicate datastream failed:', e);
        }
    };



    const fields = [
        (edit.mode === 'edit' && canDeleteDatastream) ?
            {
                field: 'actions',
                headerName: t.column.actions,
                type: 'actions',
                width: 100,
                getActions: (params) => [
                    <GridActionsCellItem
                        icon={<Tooltip title={(t.duplicateDatastream || t.duplicate || 'Duplicate')}><DuplicateIcon /></Tooltip>}
                        showInMenu
                        label={(t.duplicateDatastream || t.duplicate || 'Duplicate')}
                        onClick={() => handleDuplicateDatastream(params.row.id)}
                    />,
                    <GridActionsCellItem
                        icon={<Tooltip title={t.delete}><DeleteIcon /></Tooltip>}
                        showInMenu
                        label={t.delete}
                        onClick={() => {
                            setDetails(prev => ({
                                ...prev,
                                datastreams: prev.datastreams?.filter(ds => ds.id !== params.row.id)
                            }));
                            setTemplateEvaluationData(prevData => ({
                                ...prevData,
                                datastream_ranges: prevData.datastream_ranges.filter(r => r.datastream_id !== params.row.id)
                            }));
                        }}
                    />,
                ],
            } : {
                field: 'actions',
                headerName: t.column.actions,
                width: 100,
                cellClassName: 'actions-disabled',
                renderCell: () => null
            },
        {
            field: 'id',
            headerName: t.column.id,
            width: 100,
            align: 'left',
            headerAlign: 'left',
            type: 'number',
            renderCell: (params) => formatDatastreamIdForDisplay(params.row.id),
        },
        { field: 'name', headerName: t.column.name, width: 200, align: 'left', headerAlign: 'left' },
        { field: 'alias', headerName: t.column.alias, width: 200, align: 'center', headerAlign: 'center' },
        {
            field: 'color', headerName: t.column.color, align: 'center',
            headerAlign: 'center', renderCell: (params) => {
                return (
                    <div
                        style={{
                            position: 'relative',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '25px',
                            width: '25px',

                            backgroundColor: params.row.color,

                        }}
                    >
                    </div>
                );
            },
        },
        { field: 'pin', headerName: t.column.pin, width: 150, align: 'center', headerAlign: 'center' },
        { field: 'dataType', headerName: t.column.datatype, width: 100, align: 'center', headerAlign: 'center' },
        { field: 'unit', headerName: t.column.unit, width: 100, align: 'center', headerAlign: 'center' },
        { field: 'isRaw', headerName: t.column.israw, width: 100, align: 'center', headerAlign: 'center' },
        { field: 'min', headerName: t.column.min, width: 100, align: 'center', headerAlign: 'center' },
        { field: 'max', headerName: t.column.max, width: 100, align: 'center', headerAlign: 'center' },
        { field: 'decimals', headerName: t.column.decimals, width: 100, align: 'center', headerAlign: 'center' },
        { field: 'defaultValue', headerName: t.column.defaultvalue, width: 100, align: 'center', headerAlign: 'center' },
        {
            field: 'condition', headerName: t.column.condition, width: 100, align: 'center',
            headerAlign: 'center', renderCell: (params) => {

                return (
                    params.row.condition ?
                        <ToggleOn style={{
                            color: 'var(--accent)', fontSize: 'calc(var(--icon-size) * var(--font-size-multiplier))', cursor: 'not-allowed', opacity: .5
                        }} /> :
                        <ToggleOff style={{
                            color: colors.cellColor, fontSize: 'calc(var(--icon-size) * var(--font-size-multiplier))', cursor: 'not-allowed', opacity: .5
                        }} />
                );
            },
        },
        {
            field: 'action', headerName: t.column.action, width: 100, align: 'center',
            headerAlign: 'center', renderCell: (params) => {

                return (
                    params.row.action ?
                        <ToggleOn style={{
                            color: 'var(--accent)', fontSize: 'calc(var(--icon-size) * var(--font-size-multiplier))', cursor: 'not-allowed', opacity: .5
                        }} /> :
                        <ToggleOff style={{
                            color: colors.cellColor, fontSize: 'calc(var(--icon-size) * var(--font-size-multiplier))', cursor: 'not-allowed', opacity: .5
                        }} />
                );
            },
        },
        {
            field: 'isControlled', headerName: t.column.isControlled || 'Controlled', width: 120, align: 'center',
            headerAlign: 'center', renderCell: (params) => {

                return (
                    params.row.isControlled ?
                        <ToggleOn style={{
                            color: 'var(--accent)', fontSize: 'calc(var(--icon-size) * var(--font-size-multiplier))', cursor: 'not-allowed', opacity: .5
                        }} /> :
                        <ToggleOff style={{
                            color: colors.cellColor, fontSize: 'calc(var(--icon-size) * var(--font-size-multiplier))', cursor: 'not-allowed', opacity: .5
                        }} />
                );
            },
        },
        
    ];

    const handleRowClick = (params) => {
        setEdit((prev) => ({ ...prev, edit: true, dataId: parseInt(params.row.id) }));
    };

    const eventRowClick = (params) => {
        setEdit((prev) => ({ ...prev, editEvents: true, eventId: parseInt(params.row.id) }));
    };

    const [selectedDatastreamRows, setSelectedDatastreamRows] = useState([]);
    const [selectedEventRows, setSelectedEventRows] = useState([]);

    const handleMultiDelete = (ids) => {
        setDetails(prev => ({
            ...prev,
            datastreams: prev.datastreams?.filter(ds => !ids.includes(ds.id))
        }));
         setTemplateEvaluationData(prevData => ({
             ...prevData,
             datastream_ranges: prevData.datastream_ranges.filter(r => !ids.includes(r.datastream_id))
         }));
        setSelectedDatastreamRows([]);
    };
    let selectActions = [
        canDeleteTemplate ?
            {
                title: t.delete,
                color: 'rgb(236, 69, 78)',
                icon: <DeleteIcon color={'rgb(236, 69, 78)'} />,
                action: (ids) => { handleMultiDelete(ids); }
            } : null
    ];
    const tabs = useMemo(() => {
        const availableTabs = [];
        availableTabs.push({ name: t.tabs.home, icon: <HomeIcon /> });
        if (canViewDatastreams) {
            availableTabs.push({ name: t.tabs.datastreams, icon: <DatastreamsIcon /> });
        }
        availableTabs.push({ name: t.tabs.webdashboard, icon: <WebDashboardIcon /> });
        if (canViewEvents) {
            availableTabs.push({ name: t.tabs.eventsnotifications, icon: <EventNotificationIcon /> });
        }
        return availableTabs;
    }, [t.tabs, canViewDatastreams, canViewEvents]);


    const [currentTab, setCurrentTab] = useState(() => {
        const tabFromUrl = parseInt(searchParams.get('tab'));
        return !isNaN(tabFromUrl) ? tabFromUrl : 0;
    });

    const [currentDashboard, setCurrentDashboard] = useState(() => {
        const dashboardIdxFromUrl = parseInt(searchParams.get('dashboardIdx'));
        return !isNaN(dashboardIdxFromUrl) ? dashboardIdxFromUrl : 0;
    });

    const registerFixedWidthToggleHandler = useCallback((handler) => {
        if (typeof handler === 'function') {
            if (fixedWidthToggleHandlerRef.current === handler) return;
            fixedWidthToggleHandlerRef.current = handler;
            setIsFixedWidthToggleReady((prev) => prev || true);
            return;
        }

        if (fixedWidthToggleHandlerRef.current === null) return;
        fixedWidthToggleHandlerRef.current = null;
        setIsFixedWidthToggleReady((prev) => prev && false);
    }, []);

    const handleFixedWidthDashboardToggle = useCallback((nextChecked) => {
        if (typeof fixedWidthToggleHandlerRef.current === 'function') {
            fixedWidthToggleHandlerRef.current(nextChecked);
        }
    }, []);

    const handleFixedWidthStateChange = useCallback((nextChecked) => {
        setIsCurrentDashboardFixedWidth((prev) => (prev === nextChecked ? prev : nextChecked));
    }, []);

    const registerMultiSelectToggleHandler = useCallback((handler) => {
        if (typeof handler === 'function') {
            if (multiSelectToggleHandlerRef.current === handler) return;
            multiSelectToggleHandlerRef.current = handler;
            setIsMultiSelectToggleReady((prev) => prev || true);
            return;
        }

        if (multiSelectToggleHandlerRef.current === null) return;
        multiSelectToggleHandlerRef.current = null;
        setIsMultiSelectToggleReady((prev) => prev && false);
    }, []);

    const handleMultiSelectDashboardToggle = useCallback((nextChecked) => {
        if (typeof multiSelectToggleHandlerRef.current === 'function') {
            multiSelectToggleHandlerRef.current(nextChecked);
        }
    }, []);

    const handleMultiSelectStateChange = useCallback((nextChecked) => {
        setIsMultiSelectEnabled((prev) => (prev === nextChecked ? prev : nextChecked));
    }, []);

    useEffect(() => {
        const nextFixedWidth = getEffectiveFixedWidthValue(details?.webdashboard?.[currentDashboard]);
        setIsCurrentDashboardFixedWidth((prev) => (prev === nextFixedWidth ? prev : nextFixedWidth));
    }, [details?.webdashboard, currentDashboard]);

    const canReorderDashboardTabs = useMemo(() => {
        if (typeof window === 'undefined') {
            return false;
        }

        const hasCoarsePointer =
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(pointer: coarse)').matches;
        const touchPoints =
            typeof navigator !== 'undefined'
                ? Number(navigator.maxTouchPoints || 0)
                : 0;

        return !hasCoarsePointer && touchPoints === 0;
    }, []);


    const [addDashboard, setAddDashboard] = useState({
        adding: false,
        dashboardName: 'Dashboard',
        dashboardRoles: ['super', 'owner'],
        dashboardLayoutMode: WEB_DASHBOARD_LAYOUT_MODES.PIXELS,
    });
    const [editingTabRoleData, setEditingTabRoleData] = useState(null);

    const [editPopupAnchor, setEditPopupAnchor] = useState(null);
    const [addPopupAnchor, setAddPopupAnchor] = useState(null);

    const editPopupRef = useRef(null);
    const addPopupRef = useRef(null);
    const editTriggerRef = useRef(null);
    const addTriggerRef = useRef(null);

    const handleEditTabRoleSelection = useCallback((role) => {
        setEditingTabRoleData(prev => {
            if (!prev) return prev;

            let newRoles = new Set(prev.roles);

            if (['super', 'owner'].includes(role)) {
                return prev; 
            }

            if (newRoles.has(role)) {
                newRoles.delete(role);
            } else {
                newRoles.add(role);
            }

          newRoles.add('super');
            newRoles.add('owner');

            return { ...prev, roles: Array.from(newRoles) };
        });
    }, []);

    const handleEditTabNameChange = useCallback((value) => {
        setEditingTabRoleData(prev => {
            if (!prev) return prev;
            return { ...prev, name: value };
        });
    }, []);

   const saveEditedTabRoles = () => {
        if (!editingTabRoleData) return;

        setDetails(prevDetails => {
            const updatedWebDashboard = [...(prevDetails.webdashboard || [])];
            const current = updatedWebDashboard[editingTabRoleData.index];
            if (!current) return prevDetails;

            const nextNameRaw = typeof editingTabRoleData.name === 'string' ? editingTabRoleData.name.trim() : '';
            const nextName = nextNameRaw.length > 0 ? nextNameRaw : current.name;

            updatedWebDashboard[editingTabRoleData.index] = {
                ...current,
                name: nextName,
                access_role: editingTabRoleData.roles
            };

            return { ...prevDetails, webdashboard: updatedWebDashboard };
        });

        setEditingTabRoleData(null);
        setEditPopupAnchor(null);
    };

    


    const handleRoleSelection = (role) => {
        setAddDashboard(prev => {
            let newRoles = new Set(prev.dashboardRoles);

            if (['super', 'owner'].includes(role)) {
                return prev; 
            }

            if (newRoles.has(role)) {
                newRoles.delete(role);
            } else {
                newRoles.add(role);
            }

            newRoles.add('super');
            newRoles.add('owner');

            return { ...prev, dashboardRoles: Array.from(newRoles) };
        });
    };


    const open = useContext(CollapseContext);
    const maxWidth = `200px`;
    const isWebDashboardTabActive =
        tabs[currentTab]?.name === t.tabs.webdashboard &&
        Array.isArray(details?.webdashboard) &&
        details.webdashboard.length > 0;
    const fallbackDashboardHeight = Math.max(
        280,
        dims.height - (edit.mode === 'edit' ? 332 : 300),
    );
    const dashboardMaxHeight = `${dashboardViewportHeight || fallbackDashboardHeight}px`;

    const updateDashboardViewportHeight = useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const panelElement = dashboardBodyRef.current;
        const viewportElement = dashboardViewportRef.current;

        if (!panelElement || !viewportElement) {
            setDashboardViewportHeight(null);
            return;
        }

        const panelRect = panelElement.getBoundingClientRect();
        const viewportRect = viewportElement.getBoundingClientRect();
        const spacing = 8;
        const nextHeight = Math.floor(panelRect.bottom - viewportRect.top - spacing);

        if (!Number.isFinite(nextHeight) || nextHeight <= 0) {
            setDashboardViewportHeight(null);
            return;
        }

        setDashboardViewportHeight((prev) => (prev === nextHeight ? prev : nextHeight));
    }, []);

    useEffect(() => {
        if (!isWebDashboardTabActive) {
            setDashboardViewportHeight(null);
            return undefined;
        }

        if (typeof window === 'undefined') {
            return undefined;
        }

        let rafId = 0;
        const scheduleViewportMeasure = () => {
            if (rafId) {
                window.cancelAnimationFrame(rafId);
            }

            rafId = window.requestAnimationFrame(() => {
                updateDashboardViewportHeight();
            });
        };

        scheduleViewportMeasure();

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', scheduleViewportMeasure);

            return () => {
                if (rafId) {
                    window.cancelAnimationFrame(rafId);
                }
                window.removeEventListener('resize', scheduleViewportMeasure);
            };
        }

        const resizeObserver = new ResizeObserver(() => {
            scheduleViewportMeasure();
        });

        [dashboardBodyRef.current, dashboardViewportRef.current]
            .filter(Boolean)
            .forEach((element) => resizeObserver.observe(element));

        window.addEventListener('resize', scheduleViewportMeasure);

        return () => {
            if (rafId) {
                window.cancelAnimationFrame(rafId);
            }
            resizeObserver.disconnect();
            window.removeEventListener('resize', scheduleViewportMeasure);
        };
    }, [
        isWebDashboardTabActive,
        currentDashboard,
        edit.mode,
        updateDashboardViewportHeight,
    ]);


    function moveBorder(className, originalIndex) { 
        const border = document.querySelector('.' + className + '-tab-border');
        const targetTab = document.querySelector(`.${className}-tab[data-original-index="${originalIndex}"]`);

        if (border && targetTab) { 
            const dashboardWidth = document.defaultView.getComputedStyle(targetTab).width;
            border.style.width = dashboardWidth;
            border.style.left = targetTab.offsetLeft + 'px';
        }
    }

    const fetchTemplate = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const res = await axios.get(apiUrl(`template/examples/get/${id}`));

            if (res.data && res.data.status === 'OK') {
                const templateData = res.data.data;

                const parsedDatastreams = normalizeTemplateDatastreamCollection(
                    typeof templateData.datastreams === 'string'
                        ? JSON.parse(templateData.datastreams)
                        : (templateData.datastreams || [])
                );
                const parsedEvents = (typeof templateData.events === 'string' ? JSON.parse(templateData.events) : (templateData.events || [])).filter(ev => ev);

                const allWebdashboardsRaw = (typeof templateData.webdashboard === 'string' ? JSON.parse(templateData.webdashboard) : (templateData.webdashboard || [])).filter(wd => wd);
                const processedWebdashboards = normalizeWebDashboardTabs(allWebdashboardsRaw);

                const parsedMobdashboard = (typeof templateData.mobdashboard === 'string' ? JSON.parse(templateData.mobdashboard) : (templateData.mobdashboard || [])).filter(mobd => mobd);

                const evaluationData = templateData.templateEvaluationData || { evaluation_names: [], datastream_ranges: [] };
                setTemplateEvaluationData(evaluationData);
                const finalTemplateState = {
                    ...templateData,
                    id: templateData.template_example_id ?? templateData.id,
                    datastreams: parsedDatastreams,
                    events: parsedEvents,
                    webdashboard: processedWebdashboards,
                    mobdashboard: parsedMobdashboard,
                    templateDevices: [],
                    devices: []
                };

                const hydrated = { ...finalTemplateState, templateEvaluationData: evaluationData };
                setDetails(deepClone(hydrated));
                setInitialTemplate(deepClone(hydrated));
            } else {
                throw new Error('Received an invalid response from the server.');
            }
        } catch (err) {
            console.error("Failed to fetch template example:", err);
            const errorMessage = getErrorMessage(err);
            showAlert('error', `${t.loadTemplateExampleError || 'Could not load template example:'} ${errorMessage}`);
            setDetails(null);
            setInitialTemplate(null);
        } finally {
            setLoading(false);
        }
    }, [
        id,
        showAlert,
        t.loadTemplateExampleError,
    ]);


    

    useEffect(() => {
        if (!details?.webdashboard || details.webdashboard.length === 0) {
            if (currentDashboard !== 0) setCurrentDashboard(0);
            if (searchParams.get('dashboardIdx') !== '0') setSearchParams(prev => { prev.set('dashboardIdx', '0'); return prev; }, { replace: true });
            return;
        }

        const currentDashboardData = details.webdashboard[currentDashboard];
        const isCurrentValid = currentDashboardData && checkTabPermission(userRole, currentDashboardData.access_role);

        let newDashboardIndex = currentDashboard;
        if (!isCurrentValid) {
            const firstViewableIndex = details.webdashboard.findIndex((wd) =>
                checkTabPermission(userRole, wd.access_role)
            );
            newDashboardIndex = firstViewableIndex !== -1 ? firstViewableIndex : 0; 
        }

        if (newDashboardIndex !== currentDashboard) {
            setCurrentDashboard(newDashboardIndex);
            setSearchParams(prev => { prev.set('dashboardIdx', newDashboardIndex.toString()); return prev; }, { replace: true });
        } else {
            const dashboardIdxFromUrl = parseInt(searchParams.get('dashboardIdx'));
            if (isNaN(dashboardIdxFromUrl) || dashboardIdxFromUrl !== currentDashboard) {
                 setSearchParams(prev => { prev.set('dashboardIdx', currentDashboard.toString()); return prev; }, { replace: true });
            }
        }
    }, [details?.webdashboard, userRole, currentDashboard, searchParams, setSearchParams]);


    useEffect(() => {
        if (tabs[currentTab]?.name === t.tabs.webdashboard) {
            setTimeout(() => {
                moveBorder('dashboard', currentDashboard);
            }, 0);
        }
    }, [currentDashboard, currentTab, tabs, t.tabs.webdashboard]);

    useEffect(() => {
        fetchTemplate();
    }, [fetchTemplate]);


    const duplicateTemplate = async () => {
        if (!canAddTemplate) {
            showAlert('error', t.actionPermissionError || 'You do not have permission to perform this action.');
            return;
        }
        if (loading || !initialTemplate) {
            showAlert('warning', t.duplicateMissingTemplate || 'Cannot duplicate because the initial template data is missing.');
            return;
        }
        setLoading(true);

        try {
            const {
                name,
                example_name,
                datastreams,
                events,
                webdashboard,
                mobdashboard,
            } = initialTemplate;

            const body = {
                name: name ? `${name}-Copy` : '',
                example_name: example_name ? `${example_name}-Copy` : '',
                datastreams: datastreams || [],
                events: events || [],
                webdashboard: webdashboard || [],
                mobdashboard: mobdashboard || [],
            };


            const res = await axios.post(
                apiUrl(`template/examples/create`),
                body,
                { headers: { "Content-Type": 'application/json' } }
            );

            if (res.status === 201 && res.data.status === 'OK') {
                const createdId = res.data.data?.template_example_id ?? res.data.data?.id;
                showAlert('success', t.templateExampleDuplicated || 'Template example duplicated successfully.');
                navigate(`/configuration/template-examples/details/${createdId}`);
            } else {
                throw new Error(t.unexpectedResponse || "Received an unexpected response from the server.");
            }
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            showAlert('error', errorMessage);
        } finally {
            setLoading(false);
        }
    };

const normalizeIconPath = (value) => {
  if (!value || typeof value !== "string") return null;

  let v = value.trim().split("?")[0].replace(/\\/g, "/").replace(/^\/+/, "");

  try {
    if (v.startsWith("http://") || v.startsWith("https://")) {
      const u = new URL(v);
      v = u.pathname.replace(/^\/+/, "");
    }
  } catch {}

  if (v.startsWith("icons/")) return v;

  return `icons/${v}`;
};


const extractDashboardFiles = (templateObj) => {
    const files = new Set();
    const webdashboard = templateObj?.webdashboard || [];

    for (const page of webdashboard) {
        const widgets = page?.widgets || [];
        for (const widget of widgets) {
            if (widget?.type === "background-image") {
                [
                    widget?.extra_props?.imageUrl,
                    widget?.extra_props?.lightImageUrl,
                    widget?.extra_props?.darkImageUrl,
                ].forEach((img) => {
                    const normalized = normalizeIconPath(img);
                    if (normalized) files.add(normalized);
                });
            }

            if (widget?.type === "image-button") {
                const images = widget?.extra_props?.images || [];
                for (const imgObj of images) {
                    const img = imgObj?.imageUrl;
                    const normalized = normalizeIconPath(img);
                    if (normalized) files.add(normalized);
                }
            }
        }
    }
    return files;
};

const diffRemovedFiles = (beforeTemplate, afterTemplate) => {
    const before = extractDashboardFiles(beforeTemplate);
    const after = extractDashboardFiles(afterTemplate);

    const removed = [];
    for (const f of before) {
        if (!after.has(f)) removed.push(f);
    }
    return removed;
};

async function deleteFilesBulk(files, showAlert) {
    const clean = (files || [])
        .map(normalizeIconPath)
        .filter(Boolean);

    if (clean.length === 0) return;

    try {
        const res = await axios.post(apiUrl(`file/delete`), clean);

        if (showAlert) {
            const messageType = res.status === 207 ? "warning" : "success";
            showAlert(
                messageType,
                res.data.message ||
                    (t.deletedFiles || 'Deleted {count} files.').replace('{count}', clean.length)
            );
        }
    } catch (err) {
        console.error("Bulk file deletion failed:", err);
        if (showAlert) showAlert("warning", t.imageCleanupWarning || "Template updated, but some removed images could not be deleted.");
    }
}

const updateTemplate = async (e) => {
        if (!canEditTemplate) {
            showAlert("error", t.actionPermissionError || "You do not have permission to perform this action.");
            return;
        }
        if (e.target.classList.contains("disabled") || loading) {
            return;
        }

        const datastreamValidation = validateDatastreamCollection(details?.datastreams || [], {
            invalidCollection:
                (t && t.invalidDatastreamCollection) ||
                "Datastreams payload is invalid.",
            invalidDatastream:
                (t && t.invalidDatastreamRow) ||
                "Datastream at row {row} is invalid.",
            minMaxRequired:
                (t && t.minMaxUnitRequired) ||
                "Min and Max are required and must be valid numbers.",
            outOfBounds:
                (t && t.minMaxOutOfBounds) ||
                `Min and Max must be between -${DATASTREAM_MIN_MAX_ABS_LIMIT.toLocaleString()} and ${DATASTREAM_MIN_MAX_ABS_LIMIT.toLocaleString()}.`,
            minLessThanMax:
                (t && t.minLessThanMax) || "Min must be less than Max.",
            defaultInvalid:
                (t && t.defaultValueInvalid) || "Default value must be a valid number.",
            defaultOutOfRange:
                (t && t.defaultValueOutOfRange) || "Default value must be between Min and Max.",
            duplicateTitle:
                (t && t.datastreamTitleUnique) ||
                'Datastream title "{title}" is already used by datastream "{conflict}". Please choose another title for datastream "{target}".',
            datastreamPrefix:
                (t && t.datastreamLabel) || "Datastream",
        });

        if (!datastreamValidation.valid) {
            showAlert("error", datastreamValidation.message);
            return;
        }

setLoading(true);

        try {
            const payload = await uploadWidgetsFiles();
            const removedFiles = diffRemovedFiles(initialTemplate, payload);

            const finalPayload = {
                ...payload,
                datastreams: payload.datastreams || [],
                events: payload.events || [],
                webdashboard: payload.webdashboard || [],
                mobdashboard: payload.mobdashboard || [],
            };

            const res = await axios.put(
                apiUrl(`template/examples/update/${id}`),
                finalPayload,
                { headers: { "Content-Type": "application/json" } }
            );

            if (res.status === 200 && res.data.status === "OK") {
                if (previousIcon && previousIcon.length > 0) {
                    try { 
                        await deleteFilesBulk([previousIcon], showAlert);
                            console.log("Successfully cleaned up previous icon:", previousIcon);
                     } catch (deleteErr) {    
                        console.warn("Non-critical error: Failed to delete previous icon.", deleteErr);
                }
                }

                if (removedFiles.length > 0) {
                    await deleteFilesBulk(removedFiles, showAlert);
                }

                showAlert(
                    "success",
                    res.data?.message ||
                        t.templateExampleUpdated ||
                        "Template example updated successfully."
                );

                if (origin === 'device' && originDeviceId) {
                    navigate(`/devices/details/${originDeviceId}`);
                    return;
                } else {
                    setEdit((prev) => ({ ...prev, mode: "view" }));
                    const responseData = res.data.data;
                    const updatedDetails = {
                        ...responseData,
                        id: responseData.template_example_id ?? responseData.id,
                        datastreams: normalizeTemplateDatastreamCollection(
                            typeof responseData.datastreams === "string"
                                ? JSON.parse(responseData.datastreams)
                                : (responseData.datastreams || [])
                        ),
                        events: (typeof responseData.events === "string" 
                            ? JSON.parse(responseData.events) 
                            : (responseData.events || [])
                        ).filter((ev) => ev),
                        webdashboard: normalizeWebDashboardTabs(
                            typeof responseData.webdashboard === "string"
                                ? JSON.parse(responseData.webdashboard)
                                : (responseData.webdashboard || [])
                        ),
                        mobdashboard: (typeof responseData.mobdashboard === "string" 
                            ? JSON.parse(responseData.mobdashboard) 
                            : (responseData.mobdashboard || [])
                        ).filter((mobd) => mobd),
                        templateDevices: [],
                        devices: [],
                    };

                    const safeUpdatedDetails = deepClone(updatedDetails);
                    setDetails(safeUpdatedDetails);
                    setInitialTemplate(deepClone(safeUpdatedDetails));
                    setTemplateEvaluationData(responseData.templateEvaluationData || { evaluation_names: [], datastream_ranges: [] });
                }
            } else {
                throw new Error("Received an unexpected response from the server.");
            }
        } catch (err) {
            console.error("Template example update failed:", err);
            if (!err.isUploadError) {
                const errorMessage = getErrorMessage(err);
                showAlert("error", errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

const uploadWidgetsFiles = async () => {
    try {
        const updatedDetails = {
            ...details,
            webdashboard: (details.webdashboard || []).map((page) => ({
            ...page,
            widgets: (page.widgets || []).map((widget) => ({
                ...widget,
                extra_props: widget?.extra_props ? { ...widget.extra_props } : widget?.extra_props,
            })),
        })),
        };

        if (updatedDetails.webdashboard) {
            for (const page of updatedDetails.webdashboard) {
                if (page.widgets) {
                    for (const widget of page.widgets) {
                        if (widget.type === "background-image") {
                            const isLightFileObject = widget.extra_props?.lightFile instanceof File;
                            const isDarkFileObject = widget.extra_props?.darkFile instanceof File;
                            const isFileObject = widget.extra_props?.file instanceof File;

                            if (isLightFileObject) {
                                if (widget.extra_props.lightImageUrl) {
                                    await deleteFile(widget.extra_props.lightImageUrl, showAlert);
                                }
                                widget.extra_props.lightImageUrl = await saveFile(widget.extra_props.lightFile, showAlert, 'background-image-light');
                                widget.extra_props.useThemeImages = true;
                            }
                            if (isDarkFileObject) {
                                if (widget.extra_props.darkImageUrl) {
                                    await deleteFile(widget.extra_props.darkImageUrl, showAlert);
                                }
                                widget.extra_props.darkImageUrl = await saveFile(widget.extra_props.darkFile, showAlert, 'background-image-dark');
                                widget.extra_props.useThemeImages = true;
                            }
                            if (isFileObject) {
                                if (widget.extra_props.imageUrl) {
                                    await deleteFile(widget.extra_props.imageUrl, showAlert);
                                }
                                const saved = await saveFile(widget.extra_props.file, showAlert);
                                widget.extra_props.imageUrl = saved; 
                                delete widget.extra_props.file;
                            } else if (widget.extra_props?.file) {
                                delete widget.extra_props.file;
                            }
                            if (widget.extra_props?.useThemeImages && widget.extra_props?.imageUrl) {
                                await deleteFile(widget.extra_props.imageUrl, showAlert);
                                widget.extra_props.imageUrl = null;
                            }
                            if (widget.extra_props && "lightFile" in widget.extra_props) {
                                delete widget.extra_props.lightFile;
                            }
                            if (widget.extra_props && "darkFile" in widget.extra_props) {
                                delete widget.extra_props.darkFile;
                            }
                        } else if (widget.type === "image-button" && widget.extra_props?.images) {
                            widget.extra_props.images = await Promise.all(
                                widget.extra_props.images.map(async (img) => {
                                    let imageUrl = img.imageUrl;

                                    if (img.file instanceof File) {
                                        if (img.imageUrl) {
                                            await deleteFile(img.imageUrl, showAlert);
                                        }
                                        imageUrl = await saveFile(img.file, showAlert);
                                    } else if (img.file) {
                                        delete img.file;
                                    }

                                    return { ...img, imageUrl };
                                })
                            );
                        }
                    }
                }
            }
        }
        const stripWidgetMinMaxUnit = (pages) => {
            const TYPES = new Set(['gauge', 'radial-gauge', 'number', 'slider', 'label']);
            return (pages || []).map((p) => {
                const widgets = (p?.widgets || []).map((w) => {
                    if (!w || !TYPES.has(w.type) || !w.extra_props) return w;
                    const ep = { ...(w.extra_props || {}) };
                    delete ep.min;
                    delete ep.max;
                    delete ep.unit;
                    return { ...w, extra_props: ep };
                });
                return { ...p, widgets };
            });
        };

        updatedDetails.webdashboard = stripWidgetMinMaxUnit(updatedDetails.webdashboard);

        return updatedDetails;
    } catch (uploadError) {
        console.error("An error occurred during the widget file upload process.", uploadError);
        uploadError.isUploadError = true;
        const errorMessage = getErrorMessage(uploadError);
        showAlert("error", `${t.fileSaveError || 'Failed to save changes due to a file error:'} ${errorMessage}`);
        throw uploadError;
    }
};

async function deleteFile(fileName, showAlert) {
    const normalized = normalizeIconPath(fileName);

    if (!normalized || typeof normalized !== "string" || normalized.trim() === "") {
        const errorMsg = t.invalidFilenameForDeletion || "Invalid filename provided for deletion.";
        if (showAlert) showAlert("error", errorMsg);
        throw new Error(errorMsg);
    }

    try {
        const body = [normalized]; 
        const res = await axios.post(apiUrl(`file/delete`), body);

        if (showAlert) {
            const messageType = res.status === 207 ? "warning" : "success";
            showAlert(messageType, res.data.message || t.fileDeletedSuccess || "File deleted successfully.");
        }
    } catch (err) {
        console.error("File deletion failed:", err);
        if (showAlert) showAlert("error", getErrorMessage(err));
        throw err;
    }
}

async function saveFile(file, showAlert, prefix = 'background-image') {
  if (!(file instanceof File)) {
    const errorMsg = t.invalidUploadFile || "Invalid file provided for upload.";
    if (showAlert) showAlert("error", errorMsg);
    throw new Error(errorMsg);
  }

  const suffix = file.name.split(".").pop();
  const newFileName = `${prefix}-${Date.now()}.${suffix}`;

  const formData = new FormData();
  formData.append("file", file);
  formData.append("fileName", newFileName);
  formData.append("type", "icons");

  try {
    const res = await axios.post(apiUrl("file/upload"), formData);
    if (showAlert) showAlert("success", res.data.message || t.fileUploadedSuccess || "File uploaded successfully.");

    return newFileName;
  } catch (err) {
    console.error("File upload failed:", err);
    if (showAlert) showAlert("error", getErrorMessage(err));
    throw err;
  }
}



const handleCancelEdit = async () => {
        const confirmed = await alert({
            type: 'confirm',
            variant: 'danger',
            icon: 'alert',
            title: t.confirmTitle || 'Confirm',
            message: t.leaveWithoutSaving,
            confirmText: t.buttonOk || templateTranslations[lang].buttonOk || 'OK',
            cancelText: t.cancel || templateTranslations[lang].buttonCancel || 'Cancel',
            showCancel: true,
        });

        if (!confirmed) {
            return;
        }

        if (details && initialTemplate) {
            const newIconWasUploaded =
                details.icon &&
                details.icon.length > 0 &&
                details.icon !== initialTemplate.icon;

            if (newIconWasUploaded) {
                try {
                    const body = ['/icons/' + details.icon];
                    await axios.post(apiUrl(`file/delete`), body);
                    console.log(
                        'Orphaned icon cleanup successful:',
                        details.icon
                    );
                } catch (err) {
                    console.warn(
                        'Non-critical error: Failed to clean up orphaned icon.',
                        err
                    );
                }
            }
        }

        if (origin === 'device' && originDeviceId) {
            navigate(`/devices/details/${originDeviceId}`);
            return;
        } else {
            setEdit(prev => ({ ...prev, mode: 'view' }));
            setDetails(initialTemplate);
            setTemplateEvaluationData(
                initialTemplate.templateEvaluationData || {
                    evaluation_names: [],
                    datastream_ranges: []
                }
            );
        }
    };


    const size = { width: '100%', height: '60vh' };
    const isApplyDisabled = useMemo(() => {
        if (!details || !initialTemplate) {
            return true;
        }

        const initialEvaluationData = initialTemplate.templateEvaluationData || {
            evaluation_names: [],
            datastream_ranges: []
        };

        return (
            JSON.stringify(details) === JSON.stringify(initialTemplate) &&
            JSON.stringify(templateEvaluationData) === JSON.stringify(initialEvaluationData)
        );
    }, [details, initialTemplate, templateEvaluationData]);

useEffect(() => {
        const onPointerDown = (e) => {
            const target = e.target;

            if (editingTabRoleData) {
                const clickedInsideEdit =
                    editPopupRef.current && editPopupRef.current.contains(target);
                const clickedEditTrigger =
                    editTriggerRef.current && editTriggerRef.current.contains(target);

                if (!clickedInsideEdit && !clickedEditTrigger) {
                    setEditingTabRoleData(null);
          setEditPopupAnchor(null);
                    setEditPopupAnchor(null);
                }
            }

            if (addDashboard.adding) {
                const clickedInsideAdd =
                    addPopupRef.current && addPopupRef.current.contains(target);
                const clickedAddTrigger =
                    addTriggerRef.current && addTriggerRef.current.contains(target);

                if (!clickedInsideAdd && !clickedAddTrigger) {
                    setAddDashboard({
                        adding: false,
                        dashboardName: 'Dashboard',
                        dashboardRoles: ['super', 'owner'],
                        dashboardLayoutMode: WEB_DASHBOARD_LAYOUT_MODES.PIXELS,
                    });
                    setAddPopupAnchor(null);
                }
            }
        };

        window.addEventListener('pointerdown', onPointerDown, true);
        return () => window.removeEventListener('pointerdown', onPointerDown, true);
    }, [editingTabRoleData, addDashboard.adding]);


    const hasHeaderPrimaryActions =
        canDownloadReports ||
        canAddTemplate ||
        (canAddTemplateExamples && edit.mode !== 'edit');
    const hasHeaderDeleteAction = canDeleteTemplate;
    const hasHeaderOverflowActions =
        hasHeaderPrimaryActions ||
        hasHeaderDeleteAction ||
        canEditTemplate;

    return (canViewTemplate ?
        loading ? <Spinner /> :
            details?.id ?
                <Fragment>
                    <Alert
                        open={alertState.open}
                        close={() => setAlertState({ ...alertState, open: false })}
                        message={alertState.message}
                        type={alertState.type}
                        position={alertState.position}
                    />
                    <AppDialog
                        open={dialog.open}
                        type={dialog.type}
                        variant={dialog.variant}
                        icon={dialog.icon}
                        title={dialog.title}
                        message={dialog.message}
                        confirmText={dialog.confirmText}
                        cancelText={dialog.cancelText}
                        showCancel={dialog.showCancel}
                        onConfirm={() => closeDialog(true)}
                        onCancel={() => closeDialog(false)}
                        onClose={() => closeDialog(false)}
                    />

                    {typeof document !== 'undefined' && markAsExampleDialog.open
                        ? createPortal(
                              <div
                                  id="modal"
                                  onMouseDown={() => {
                                      if (!markAsExampleDialog.submitting) {
                                          closeMarkAsExampleDialog();
                                      }
                                  }}
                              >
                                  <div
                                      id="popup"
                                      className="dashboard-modal td-example-modal"
                                      role="dialog"
                                      aria-modal="true"
                                      aria-labelledby="template-example-modal-title"
                                      onMouseDown={(event) => event.stopPropagation()}
                                  >
                                      <div className="dashboard-header td-example-modal__header">
                                          <div className="dashboard-header-icon">
                                              <TemplateIcon />
                                          </div>

                                          <div className="dashboard-header-text">
                                              <h2 id="template-example-modal-title">
                                                  {t.markAsExample || 'Mark as Example'}
                                              </h2>
                                              <p>
                                                  {t.markAsExampleDescription ||
                                                      'Choose a unique example name and we will copy this template into the examples library.'}
                                              </p>
                                          </div>

                                          <button
                                              type="button"
                                              className="td-example-modal__close"
                                              onClick={closeMarkAsExampleDialog}
                                              disabled={markAsExampleDialog.submitting}
                                              aria-label={t.close || 'Close'}
                                          >
                                              <CloseIcon fontSize="small" />
                                          </button>
                                      </div>

                                      <div className="modal-divider" />

                                      <div className="modal-content dashboard-content td-example-modal__content">
                                          <div className="form-item">
                                              <label
                                                  className="form-lbl"
                                                  htmlFor="template-example-name"
                                                  style={{ color: 'var(--header-color)', marginLeft: 0, marginRight: 0 }}
                                              >
                                                  {t.exampleNameLabel || 'Example Name'}
                                              </label>
                                              <input
                                                  id="template-example-name"
                                                  type="text"
                                                  className="add-column td-example-modal__input"
                                                  placeholder={t.exampleNamePlaceholder || 'Enter a unique example name'}
                                                  value={markAsExampleDialog.exampleName}
                                                  onChange={handleMarkAsExampleNameChange}
                                                  onKeyDown={(event) => {
                                                      if (event.key === 'Enter') {
                                                          event.preventDefault();
                                                          if (!isMarkAsExampleSubmitDisabled) {
                                                              submitMarkAsExample();
                                                          }
                                                      }
                                                  }}
                                                  autoFocus
                                                  maxLength={100}
                                                  disabled={markAsExampleDialog.submitting}
                                              />
                                              <div className="td-example-modal__helper">
                                                  {loadingTemplateExamples
                                                      ? (t.markAsExampleChecking || 'Checking existing examples...')
                                                      : (t.markAsExampleHelper || 'This name must be unique across all template examples.')}
                                              </div>
                                              {isExampleNameDuplicate && !markAsExampleDialog.error ? (
                                                  <div className="td-example-modal__error">
                                                      {t.markAsExampleUnique || 'Example name must be unique.'}
                                                  </div>
                                              ) : null}
                                              {markAsExampleDialog.error ? (
                                                  <div className="td-example-modal__error">
                                                      {markAsExampleDialog.error}
                                                  </div>
                                              ) : null}
                                          </div>

                                          <div className="td-example-modal__copy-card">
                                              <div className="td-example-modal__copy-title">
                                                  {t.markAsExampleCopiedFields || 'Copied from this template'}
                                              </div>
                                              <div className="td-example-modal__copy-list">
                                                  {[
                                                      t.exampleCopyName || 'Name',
                                                      t.exampleCopyDatastreams || 'Datastreams',
                                                      t.exampleCopyWebDashboard || 'Web Dashboard',
                                                      t.exampleCopyMobileDashboard || 'Mobile Dashboard',
                                                      t.exampleCopyEvents || 'Events',
                                                  ].map((label) => (
                                                      <span key={label} className="td-example-modal__copy-pill">
                                                          {label}
                                                      </span>
                                                  ))}
                                              </div>
                                          </div>
                                      </div>

                                      <div className="modal-divider" />

                                      <div className="modal-footer dashboard-footer">
                                          <button
                                              className="btn btn-cancel-details"
                                              onClick={closeMarkAsExampleDialog}
                                              disabled={markAsExampleDialog.submitting}
                                          >
                                              {t.cancel || 'Cancel'}
                                          </button>

                                          <div className="modal-footer-right-buttons">
                                              <button
                                                  className="btn btn-apply"
                                                  onClick={submitMarkAsExample}
                                                  disabled={isMarkAsExampleSubmitDisabled}
                                              >
                                                  {markAsExampleDialog.submitting
                                                      ? (t.markAsExampleSaving || 'Saving...')
                                                      : (t.markAsExampleSave || 'Save as Example')}
                                              </button>
                                          </div>
                                      </div>
                                  </div>
                              </div>,
                              document.body
                          )
                        : null}

                    {typeof document !== 'undefined' && editingTabRoleData && editPopupAnchor
                        ? createPortal(
                              <div
                                  ref={editPopupRef}
                                  className="role-editor-popup role-editor-popup--portal"
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                      position: 'fixed',
                                      top: editPopupAnchor.top,
                                      left: editPopupAnchor.left,
                                      transform: 'translateX(-50%)',
                                      zIndex: 100000,
                                      backgroundColor: 'var(--cell-background)',
                                      border: '1px solid var(--modal-border)',
                                      borderRadius: '8px',
                                      padding: '10px',
                                      boxShadow: '0 12px 30px rgba(0,0,0,0.35)',
                                      width: '320px',
                                      maxWidth: 'calc(100vw - 24px)',
                                      maxHeight: 'calc(100vh - 24px)',
                                      overflowY: 'auto',
                                      overflowX: 'hidden',
                                  }}
                              >
                                  <h4
                                      style={{
                                          marginBottom: '10px',
                                          fontSize:
                                              'calc(var(--ui-font-size) * var(--font-size-multiplier))',
                                      }}
                                  >
                                      {t.editRolesTitle}
                                  </h4>

                                  <div style={{ marginBottom: 10 }}>
                                      <div
                                          style={{
                                              fontSize: 12,
                                              fontWeight: 700,
                                              color: '#AEEAFF',
                                              marginBottom: 8,
                                          }}
                                      >
                                          {t.tabName || 'Tab name'}

                                          {details.webdashboard?.length > 0 ? (
                                          <Tooltip title={t.dashboardDeleteCurrentTab}>
                                              <button
                                                  type="button"
                                                  className="details-delete-btn"
                                                  style={{ position: 'absolute', flexShrink: 0, marginRight: '8px', right:'2%' }}
                                                  onClick={async (e) => {
                                                      e.stopPropagation();
                                                      const targetIndex = editingTabRoleData?.index;
                                                      if (targetIndex == null || targetIndex < 0) return;

                                                      const confirmed = await alert({
                                                          type: 'confirm',
                                                          variant: 'danger',
                                                          icon: 'delete',
                                                          title: t.confirmTitle || 'Confirm',
                                                          message: t.dashboardDeleteTabWarning,
                                                          confirmText: t.delete || templateTranslations[lang].delete || 'Delete',
                                                          cancelText: t.cancel || templateTranslations[lang].buttonCancel || 'Cancel',
                                                          showCancel: true,
                                                      });
                                                      if (!confirmed) return;

                                                      const totalTabs = details.webdashboard?.length || 0;
                                                      let nextIndex = currentDashboard;
                                                      if (totalTabs <= 1) {
                                                          nextIndex = 0;
                                                      } else if (targetIndex < currentDashboard) {
                                                          nextIndex = Math.max(0, currentDashboard - 1);
                                                      } else if (targetIndex === currentDashboard) {
                                                          nextIndex = currentDashboard > 0 ? currentDashboard - 1 : 0;
                                                      }

                                                      setDetails(prevDetails => {
                                                          const newWebdashboards = (prevDetails.webdashboard || []).filter(
                                                              (_, index) => index !== targetIndex
                                                          );
                                                          return {
                                                              ...prevDetails,
                                                              webdashboard: newWebdashboards
                                                          };
                                                      });

                                                      setEditingTabRoleData(null);
                                                      setEditPopupAnchor(null);

                                                      setCurrentDashboard(nextIndex);
                                                      setSearchParams(prev => {
                                                          prev.set('dashboardIdx', nextIndex.toString());
                                                          return prev;
                                                      }, { replace: true });
                                                  }}
                                              >
                                                  <DeleteIcon color={'var(--root-background)'} />
                                              </button>
                                          </Tooltip>
                                      ) : <span /> }
                                      </div>
                                      <input
                                          type="text"
                                          className="add-column"
                                          maxLength={50}
                                          value={editingTabRoleData.name || ''}
                                          onChange={(e) =>
                                              handleEditTabNameChange(e.target.value)
                                          }
                                          style={{
                                              paddingInlineStart: '10px',
                                              color: 'var(--cell-color)',
                                              caretColor: 'var(--cell-color)',
                                              width: '100%',
                                          }}
                                      />
                                  </div>

                                  <div style={{ marginBottom: 10 }}>
                                      <WebDashboardLayoutModeField
                                          value={editingTabRoleData.layoutMode}
                                          title={webDashboardT.layoutModeLabel || 'Layout Mode'}
                                          helperText={
                                              webDashboardT.layoutModeReadonlyHelper ||
                                              'This layout mode is locked after the tab is created.'
                                          }
                                          translations={webDashboardT}
                                          readOnly
                                          compact
                                      />
                                  </div>

                                  <div
                                      className="multi-select-container"
                                      style={{ maxHeight: '160px', overflowY: 'auto' }}
                                  >
                                      {allRoles
                                          .filter((role) => !['super', 'owner'].includes(role))
                                          .map((role) => (
                                              <div
                                                  key={role}
                                                  onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleEditTabRoleSelection(role);
                                                  }}
                                                  style={{
                                                      padding: '4px 0',
                                                      cursor: 'pointer',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      fontSize:
                                                          'calc(var(--body-font-size) * var(--font-size-multiplier))',
                                                  }}
                                              >
                                                  <input
                                                      type="checkbox"
                                                      readOnly
                                                      checked={editingTabRoleData.roles.includes(role)}
                                                      style={{
                                                          marginRight: '8px',
                                                          cursor: 'pointer',
                                                      }}
                                                  />
                                                  {role}
                                              </div>
                                          ))}
                                  </div>

                                  <div
                                      style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          gap: '8px',
                                          marginTop: '10px',
                                      }}
                                  >

                                      <div style={{ display: 'flex', gap: '8px' }}>
                                          <button
                                              className="btn btn-cancel"
                                              style={{
                                                  fontSize:
                                                      'calc(var(--body-font-size) * var(--font-size-multiplier))',
                                                  padding: '6px 12px',
                                              }}
                                              onClick={(e) => {
                                                  e.stopPropagation();
                                                  setEditingTabRoleData(null);
                                                  setEditPopupAnchor(null);
                                              }}
                                          >
                                              {t.cancel}
                                          </button>

                                          <button
                                              className="btn edit-btn-save-role"
                                              style={{
                                                  fontSize:
                                                      'calc(var(--body-font-size) * var(--font-size-multiplier))',
                                                  padding: '6px 12px',
                                              }}
                                              onClick={(e) => {
                                                  e.stopPropagation();
                                                  saveEditedTabRoles();
                                              }}
                                          >
                                              {t.apply}
                                          </button>
                                      </div>
                                  </div>
                              </div>,
                              document.body
                          )
                        : null}

                    {typeof document !== 'undefined' && addDashboard.adding && addPopupAnchor
                        ? createPortal(
                              <div
                                  ref={addPopupRef}
                                  className="role-editor-popup role-editor-popup--add role-editor-popup--portal"
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                      position: 'fixed',
                                      top: addPopupAnchor.top,
                                      left: addPopupAnchor.left,
                                      transform: 'translateX(-50%)',
                                      zIndex: 100000,
                                      backgroundColor: 'var(--cell-background)',
                                      border: '1px solid var(--modal-border)',
                                      borderRadius: '8px',
                                      padding: '10px',
                                      boxShadow: '0 12px 30px rgba(0,0,0,0.35)',
                                      width: '340px',
                                      maxWidth: 'calc(100vw - 24px)',
                                      maxHeight: 'calc(100vh - 24px)',
                                      overflowY: 'auto',
                                      overflowX: 'hidden',
                                  }}
                              >
                                  <h4
                                      style={{
                                          marginBottom: '10px',
                                          fontSize:
                                              'calc(var(--ui-font-size) * var(--font-size-multiplier))',
                                      }}
                                  >
                                      {t.addTabTitle || t.dashboardAddTab || 'Add Tab'}
                                  </h4>

                                  <div style={{ marginBottom: 10 }}>
                                      <div
                                          style={{
                                              fontSize: 12,
                                              fontWeight: 700,
                                              color: '#AEEAFF',
                                              marginBottom: 8,
                                          }}
                                      >
                                          {t.tabName || 'Tab name'}
                                      </div>
                                      <input
                                          type="text"
                                          className="add-column"
                                          maxLength={50}
                                          value={addDashboard.dashboardName || ''}
                                          onChange={(e) =>
                                              setAddDashboard((prev) => ({
                                                  ...prev,
                                                  dashboardName: e.target.value,
                                              }))
                                          }
                                          style={{
                                              paddingInlineStart: '10px',
                                              color: 'var(--cell-color)',
                                              caretColor: 'var(--cell-color)',
                                              width: '100%',
                                          }}
                                      />
                                  </div>

                                  <div style={{ marginBottom: 10 }}>
                                      <WebDashboardLayoutModeField
                                          value={addDashboard.dashboardLayoutMode}
                                          onChange={(nextMode) =>
                                              setAddDashboard((prev) => ({
                                                  ...prev,
                                                  dashboardLayoutMode: nextMode,
                                              }))
                                          }
                                          title={webDashboardT.layoutModeLabel || 'Layout Mode'}
                                          helperText={
                                              webDashboardT.layoutModeHelper ||
                                              'Choose how widget sizes and positions are stored for this tab.'
                                          }
                                          translations={webDashboardT}
                                          compact
                                      />
                                  </div>

                                  <div
                                      className="multi-select-container"
                                      style={{ maxHeight: '200px', overflowY: 'auto' }}
                                  >
                                      {allRoles
                                          .filter((role) => !['super', 'owner'].includes(role))
                                          .map((role) => (
                                              <div
                                                  key={role}
                                                  onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleRoleSelection(role);
                                                  }}
                                                  style={{
                                                      padding: '4px 0',
                                                      cursor: 'pointer',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      fontSize:
                                                          'calc(var(--body-font-size) * var(--font-size-multiplier))',
                                                  }}
                                              >
                                                  <input
                                                      type="checkbox"
                                                      readOnly
                                                      checked={addDashboard.dashboardRoles.includes(role)}
                                                      style={{
                                                          marginRight: '8px',
                                                          cursor: 'pointer',
                                                      }}
                                                  />
                                                  {role}
                                              </div>
                                          ))}
                                  </div>

                                  <div
                                      style={{
                                          display: 'flex',
                                          justifyContent: 'flex-end',
                                          gap: '8px',
                                          marginTop: '10px',
                                      }}
                                  >
                                      <button
                                          type="button"
                                          className="btn btn-cancel"
                                          style={{
                                              fontSize:
                                                  'calc(var(--body-font-size) * var(--font-size-multiplier))',
                                              padding: '6px 12px',
                                          }}
                                          onClick={(e) => {
                                              e.stopPropagation();
                                              setAddDashboard({
                                                  adding: false,
                                                  dashboardName: 'Dashboard',
                                                  dashboardRoles: ['super', 'owner'],
                                                  dashboardLayoutMode: WEB_DASHBOARD_LAYOUT_MODES.PIXELS,
                                              });
                                              setAddPopupAnchor(null);
                                          }}
                                      >
                                          {t.cancel}
                                      </button>

                                      <button
                                          type="button"
                                          className="btn edit-btn-save"
                                          style={{
                                              fontSize:
                                                  'calc(var(--body-font-size) * var(--font-size-multiplier))',
                                              padding: '6px 12px',
                                          }}
                                          onClick={(e) => {
                                              e.stopPropagation();

                                              const trimmedName = String(
                                                  addDashboard.dashboardName || ''
                                              ).trim();

                                              const newDashboardEntry = createDefaultWebDashboardTab({
                                                  id: `new-${Date.now()}`,
                                                  name: trimmedName.length ? trimmedName : 'Dashboard',
                                                  accessRole: Array.isArray(addDashboard.dashboardRoles)
                                                      ? addDashboard.dashboardRoles
                                                      : ['super', 'owner'],
                                                  layoutMode: addDashboard.dashboardLayoutMode,
                                              });

                                              setDetails((prevDetails) => ({
                                                  ...prevDetails,
                                                  webdashboard: [
                                                      ...(prevDetails.webdashboard || []),
                                                      newDashboardEntry,
                                                  ],
                                              }));

                                              const newIndex =
                                                  (details.webdashboard?.length || 0);
                                              setCurrentDashboard(newIndex);
                                              setSearchParams(
                                                  (prev) => {
                                                      prev.set(
                                                          'dashboardIdx',
                                                          newIndex.toString()
                                                      );
                                                      return prev;
                                                  },
                                                  { replace: true }
                                              );

                                              setAddDashboard({
                                                  adding: false,
                                                  dashboardName: 'Dashboard',
                                                  dashboardRoles: ['super', 'owner'],
                                                  dashboardLayoutMode: WEB_DASHBOARD_LAYOUT_MODES.PIXELS,
                                              });
                                              setAddPopupAnchor(null);
                                          }}
                                      >
                                          {t.add}
                                      </button>
                                  </div>
                              </div>,
                              document.body
                          )
                        : null}

                    {addDevice ? (
                        <AddDevice
                            refresh={fetchTemplate}
                            setState={setAddDevice}
                            fixedTemplate={{
                                id: details?.id,
                                tid: details?.tid,
                                name: details?.name,
                                icon: details?.icon
                            }}
                        />
                    ) : null}
                    {isEvalModalOpen && canEditTemplate && (
                        <EditTemplateEvaluations
                            state={isEvalModalOpen}
                            setState={setIsEvalModalOpen}
                            templateEvaluationData={templateEvaluationData}
                            setTemplateEvaluationData={setTemplateEvaluationData}
                        />
                    )}

                    <div className='details-container'>
                        {edit.editEvents && canEditEvent && <EditEvent mode={edit.mode}
                            close={() => setEdit(prev => ({ ...prev, editEvents: false }))}
                            event={details.events?.find(ev => ev.id === edit.eventId)} 
                            setEvent={(newEvent) => setDetails(prev => ({
                                ...prev, events: prev.events?.map(ev => ev.id === newEvent.id ? newEvent : ev)
                            }))} />}
                        {addDatastream.addEvents && canEditEvent && <EditEvent mode='add'
                            close={() => setAddDatastream(prev => ({ ...prev, addEvents: false }))} addEdit event={{
                                id: (details.events?.filter(ev => ev).length ? details.events.filter(ev => ev).length + 1 : 1), 
                                name: '', code: '', type: '', color: 'rgb(0, 0, 0)', description: '',
                                notification: 0, condition: 0, action: 0
                            }} setEvent={(newEvent) => setDetails(prev => ({
                                ...prev, events: prev.events ? [...prev.events.filter(ev => ev), newEvent] : [newEvent] 
                            }))} />}
                        {edit.edit && canEditDatastream ?
                            <EditDatastream
                                state={edit}
                                setState={setEdit}
                                datastream={details.datastreams?.find(ds => ds.id === edit.dataId)} 
                                setDatastream={(newDatastream) => setDetails({
                                    ...details,
                                    datastreams: details.datastreams?.filter(ds => ds)?.map( 
                                        ds => ds.id === newDatastream.id ? newDatastream : ds)
                                })}
                                templateDatastreams={details.datastreams?.filter(ds => ds) || []}
                                templateEvaluationData={templateEvaluationData}
                                setTemplateEvaluationData={setTemplateEvaluationData}
                            /> :
                            null}
                        {addDatastream.edit ?
                            <EditDatastream
                                state={{ ...addDatastream, mode: 'edit' }}
                                setState={setAddDatastream}
                                datastream={addDatastream.initialDatastream || {
                                    id: getNextDatastreamId(details.datastreams?.filter(ds => ds) || []),
                                    max: 0,
                                    min: 0,
                                    pin: '',
                                    name: '',
                                    sync: 0,
                                    unit: '',
                                    alias: '',
                                    color: 'rgb(0, 0, 0)',
                                    isControlled: false,
                                    isRow: 0,
                                    action: 0,
                                    reports: 0,
                                    thenSet: 'no thing',
                                    dataType: 'string',
                                    decimals: '',
                                    condition: 0,
                                    invalidate: 0,
                                    customChart: 0,
                                    defaultValue: 0,
                                    enableHistory: 0,
                                    automationType: '',
                                    invalidateUnit: 'sec',
                                    invalidateValue: 0,
                                    indicator: 'custom' 
                                }}
                                setDatastream={(newDatastream) => setDetails(prev => ({ 
                                    ...prev,
                                    datastreams: prev.datastreams ? [...prev.datastreams.filter(ds => ds), newDatastream] : [newDatastream] 
                                }))}
                                templateDatastreams={details.datastreams?.filter(ds => ds) || []}
                                templateEvaluationData={templateEvaluationData} 
                                setTemplateEvaluationData={setTemplateEvaluationData} 
                            /> : null}
                            {downloadReportState.open && (
                            <DownloadTemplateReport
                                state={downloadReportState}
                                setState={setDownloadReportState}
                                template={details}
                                setLoadingReport={setLoading}
                            />
                        )}
{isMobileTemplateDetailsLayout && isMobileDetailsRailOpen ? (
    <button
        type="button"
        className="details-control-panel-backdrop"
        onClick={() => setIsMobileDetailsRailOpen(false)}
        aria-label={t.closeTabs || 'Close tabs'}
    />
) : null}
<div
    className={
        'details-control-panel' +
        (isMobileTemplateDetailsLayout ? ' details-control-panel-mobile' : '') +
        (isMobileTemplateDetailsLayout && isMobileDetailsRailOpen ? ' details-control-panel-mobile-open' : '')
    }
>
    <div className="details-control-panel-inside" />

    <div className="details-control-panel-content">
        <div
            className={`details-control-btns ${
                isDetailsRailExpanded ? 'details-control-btns-row' : 'details-control-btns-column'
            }`}
        >
            <button
                type="button"
                className="details-control-btn details-control-btn-ghost"
                onClick={() => navigate('/configuration/template-examples')}
            >
                <MenuBackButtonIcon />
            </button>

            {isMobileTemplateDetailsLayout ? (
                <button
                    type="button"
                    className="details-control-btn details-control-btn-ghost details-control-mobile-close"
                    onClick={() => setIsMobileDetailsRailOpen(false)}
                    aria-label={t.closeTabs || 'Close tabs'}
                >
                    <CloseIcon fontSize="small" />
                </button>
            ) : (
                <button
                    type="button"
                    className="details-control-btn details-control-btn-ghost"
                    onClick={() => setTabsWidth(prev => !prev)}
                >
                    <ExpandMenuIcon />
                </button>
            )}
        </div>

        <div
            className={
                'details-control-divider ' +
                (isDetailsRailExpanded
                    ? 'details-control-divider-expanded'
                    : 'details-control-divider-collapsed')
            }
        >
            {isDetailsRailExpanded ? <DividerMenuOpenedIcon /> : <DividerMenuIcon />}
        </div>

        <div
            className={
                'details-control-tabs details-control-tabs-rail ' +
                (isDetailsRailExpanded
                    ? 'details-control-tabs-expanded'
                    : 'details-control-tabs-collapsed')
            }
        >
            {tabs.map((tab, idx) => {
                const isActive = idx === currentTab;

                return (
                    <Tooltip title={tab.name} placement="right" key={idx}>
                        <div
                            className={
                                'details-control-tab details-control-tab-rail ' +
                                (isActive ? 'active ' : '') +
                                (isDetailsRailExpanded
                                    ? 'details-control-tab-rail-expanded'
                                    : 'details-control-tab-rail-collapsed')
                            }
                            onClick={() => {
                                setCurrentTab(idx);
                                setSearchParams(prev => {
                                                    prev.set('tab', idx.toString());
                                                    if (tab.name === t.tabs.webdashboard) {
                                                        if (!prev.has('dashboardIdx')) prev.set('dashboardIdx', currentDashboard.toString());
                                                    } else {
                                                        prev.delete('dashboardIdx');
                                                    }
                                                    return prev;
                                                }, { replace: true });
                                setEditingTabRoleData(null);
                                setIsMobileDetailsRailOpen(false);
                            }}
                        >
                            {isActive && (
                                <div className="details-control-tab-cyan-bar" />
                            )}

                            <span
                                className={
                                    'details-control-tab-icon ' +
                                    (isDetailsRailExpanded
                                        ? 'details-control-tab-icon-expanded'
                                        : '')
                                }
                            >
                                {tab.icon || '•'}
                            </span>

                            {isDetailsRailExpanded && (
                                <span className="details-control-tab-label">
                                    {tab.name}
                                </span>
                            )}
                        </div>
                    </Tooltip>
                );
            })}
        </div>
    </div>
</div>

                        <div className={`details-content${isWebDashboardTabActive ? ' td-details-content-dashboard' : ''}`}>
{isMobileTemplateDetailsLayout ? (
    <div className="details-mobile-rail-trigger-row">
        <button
            type="button"
            className="details-mobile-rail-trigger"
            onClick={() => setIsMobileDetailsRailOpen(true)}
            aria-label="Open tabs"
        >
            <MenuIcon fontSize="small" />
        </button>
    </div>
) : null}
<div className="details-head">
    <img
        alt="icon"
        src={`${STATIC_ICONS}${
            details.icon?.length > 0 ? details.icon : defaultIcon
        }`}
    />

    <div className="details-remain-head" style={{ width: '100%' }}>
        <div
            className="details-remain-head-part"
            style={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
            <div className="details-head-left">
                <h1>{details.example_name || details.name || 'Template Example'}</h1>

                {!isCompactHeaderActions && (
                <div className="details-inline-actions">
                    {canDownloadReports && (
                        <button
                            type="button"
                            className="details-inline-action-btn"
                            onClick={() =>
                                setDownloadReportState({
                                    open: true,
                                    template: details,
                                })
                            }
                        >
                            <DownloadReprtIcon />
                        </button>
                    )}

                    {canAddTemplate && (
                        <button
                            type="button"
                            className="details-inline-action-btn"
                            onClick={duplicateTemplate}
                        >
                            <DuplicateIcon />
                        </button>
                    )}

                    {canAddTemplateExamples && edit.mode !== 'edit' && (
                        <button
                            type="button"
                            className="details-inline-action-btn"
                            onClick={openMarkAsExampleDialog}
                            title={t.markAsExample || 'Mark as Example'}
                            aria-label={t.markAsExample || 'Mark as Example'}
                        >
                            <BookmarkAddedOutlinedIcon fontSize="small" />
                        </button>
                    )}

                    {hasHeaderPrimaryActions && canDeleteTemplate && (
                        <span className="details-inline-divider">
                            <VerticalDividerIcon />
                        </span>
                    )}

                    {canDeleteTemplate && (
                        <button
                            type="button"
                            className="details-inline-action-btn details-inline-delete-btn"
                            onClick={() =>
                                deleteWithIcon(
                                    details.id,
                                    details.icon,
                                    'template/examples',
                                    () => navigate('/configuration/template-examples'),
                                    alert,
                                )
                            }
                        >
                            <DeleteIcon />
                        </button>
                    )}
                </div>
                )}
            </div>

            <div className="details-head-right">
                {isCompactHeaderActions && hasHeaderOverflowActions ? (
                    <div className="details-head-action-group" ref={headerActionsMenuRef}>
                        <button
                            type="button"
                            className="details-inline-action-btn details-head-more-btn"
                            onClick={() => setIsHeaderActionsMenuOpen((prev) => !prev)}
                            aria-label={t.moreActions || 'More actions'}
                            aria-expanded={isHeaderActionsMenuOpen}
                        >
                            <MoreHorizIcon />
                        </button>

                        {isHeaderActionsMenuOpen ? (
                            <div className="details-actions-menu details-head-actions-menu open">
                                {canDownloadReports && (
                                    <button
                                        type="button"
                                        className="details-actions-menu-item"
                                        onClick={() => {
                                            setIsHeaderActionsMenuOpen(false);
                                            setDownloadReportState({
                                                open: true,
                                                template: details,
                                            });
                                        }}
                                    >
                                        <DownloadReprtIcon />
                                        <span>{t.downloadReport || t.download || 'Download Report'}</span>
                                    </button>
                                )}

                                {canAddTemplate && (
                                    <button
                                        type="button"
                                        className="details-actions-menu-item"
                                        onClick={() => {
                                            setIsHeaderActionsMenuOpen(false);
                                            duplicateTemplate();
                                        }}
                                    >
                                        <DuplicateIcon />
                                        <span>{t.duplicate || 'Duplicate'}</span>
                                    </button>
                                )}

                                {canAddTemplateExamples && edit.mode !== 'edit' && (
                                    <button
                                        type="button"
                                        className="details-actions-menu-item"
                                        onClick={() => {
                                            setIsHeaderActionsMenuOpen(false);
                                            openMarkAsExampleDialog();
                                        }}
                                    >
                                        <BookmarkAddedOutlinedIcon fontSize="small" />
                                        <span>{t.markAsExample || 'Mark as Example'}</span>
                                    </button>
                                )}

                                {canDeleteTemplate && (
                                    <button
                                        type="button"
                                        className="details-actions-menu-item danger"
                                        onClick={() => {
                                            setIsHeaderActionsMenuOpen(false);
                                            deleteWithIcon(
                                                details.id,
                                                details.icon,
                                                'template/examples',
                                                () => navigate('/configuration/template-examples'),
                                                alert,
                                            );
                                        }}
                                    >
                                        <DeleteIcon />
                                        <span>{t.delete || 'Delete'}</span>
                                    </button>
                                )}

                                {canEditTemplate && (hasHeaderPrimaryActions || hasHeaderDeleteAction) && (
                                    <div className="details-actions-menu-sep" />
                                )}

                                {canEditTemplate && (edit.mode === 'edit' ? (
                                    <Fragment>
                                        <button
                                            type="button"
                                            className="details-actions-menu-item"
                                            onClick={() => {
                                                setIsHeaderActionsMenuOpen(false);
                                                handleCancelEdit();
                                            }}
                                        >
                                            <span />
                                            <span>{t.cancel || 'Cancel'}</span>
                                        </button>

                                        <button
                                            type="button"
                                            className="details-actions-menu-item"
                                            disabled={isApplyDisabled}
                                            onClick={() => {
                                                if (isApplyDisabled) return;
                                                setIsHeaderActionsMenuOpen(false);
                                                updateTemplate();
                                            }}
                                        >
                                            <span />
                                            <span>{t.apply || 'Apply'}</span>
                                        </button>
                                    </Fragment>
                                ) : (
                                    <button
                                        type="button"
                                        className="details-actions-menu-item"
                                        onClick={() => {
                                            setIsHeaderActionsMenuOpen(false);
                                            setEdit((prev) => ({ ...prev, mode: 'edit' }));
                                        }}
                                    >
                                        <span />
                                        <span>{t.edit || 'Edit'}</span>
                                    </button>
                                ))}
                            </div>
                        ) : null}
                    </div>
                ) : edit.mode === 'edit' && canEditTemplate ? (
                    <div className="details-head-action-group">
                        <button
                            className="btn btn-cancel-details"
                            style={{ cursor: 'pointer' }}
                            onClick={handleCancelEdit}
                        >
                            {t.cancel}
                        </button>

                        <button
                            className={`btn asset-edit-main-btn edit-btn-save  ${
                                isApplyDisabled
                                    ? 'disabled'
                                    : ''
                            }`}
                            onClick={updateTemplate}
                        >
                            {t.apply}
                        </button>
                    </div>
                ) : canEditTemplate ? (
                    <button
                        className="btn edit-btn devices-primary-btn"
                        style={{
                            cursor: 'pointer',
                        }}
                        onClick={() =>
                            setEdit(prev => ({ ...prev, mode: 'edit' }))
                        }
                    >
                        {t.edit}
                    </button>
                ) : null}
            </div>
        </div>
    </div>
</div>
                            <div
                                ref={dashboardBodyRef}
                                className='details-body td-details-body-dashboard'
                            >
                                <div className={`details-tab-content${isWebDashboardTabActive ? ' td-details-tab-content-dashboard' : ''}`}>
                                    {tabs[currentTab].name === t.tabs.datastreams ?
                                        <TemplateDatastreamsTab
                                            details={details}
                                            setDetails={setDetails}
                                            edit={edit}
                                            setEdit={setEdit}
                                            addDatastream={addDatastream}
                                            setAddDatastream={setAddDatastream}
                                            templateEvaluationData={templateEvaluationData}
                                            setTemplateEvaluationData={setTemplateEvaluationData}
                                            isEvalModalOpen={isEvalModalOpen}
                                            setIsEvalModalOpen={setIsEvalModalOpen}
                                            canEditTemplate={canEditTemplate}
                                            canEditDatastream={canEditDatastream}
                                            canDeleteDatastream={canDeleteDatastream}
                                            t={t}
                                            pagingoptions={pagingoptions}
                                            colors={colors}
                                            handleRowClick={handleRowClick}
                                            selectedRows={selectedDatastreamRows}
                                            setSelectedRows={setSelectedDatastreamRows}
                                            selectActions={selectActions}
                                            size={size}
                                            pinCategories={pinCategories}
                                            fields={fields}
                                        /> :
                                        (tabs[currentTab].name === t.tabs.webdashboard && details.webdashboard && details.webdashboard.length > 0 ?
                                            <div ref={dashboardPanelRef} className='td-dashboard-panel'>
                                                <div className='template-dashboard-tabs-header'>
                                                    <div
                                                        className='dashboard-tabs template-dashboard-tabs-scroll'
                                                        style={{ gap: '12px' }}
                                                        onDrop={(e) => {
                                                            if (edit.mode !== 'edit' || !canReorderDashboardTabs) {
                                                                return;
                                                            }

                                                            const dragIdx = parseInt(e.dataTransfer.getData('text/plain')),
                                                                targetTabElement = e.target.closest('.dashboard-tab');
                                                            if (!targetTabElement) return;
                                                            const dropIdx = parseInt(targetTabElement.dataset.originalIndex);

                                                            if (dragIdx >= 0 && dropIdx >= 0 && dragIdx !== dropIdx) { 
                                                                setDetails(prevDetails => {
                                                                    const wdb = [...(prevDetails.webdashboard || [])];
                                                                    [wdb[dragIdx], wdb[dropIdx]] = [wdb[dropIdx], wdb[dragIdx]];
                                                                    return { ...prevDetails, webdashboard: wdb };
                                                                });
                                                                setCurrentDashboard(dropIdx);
                                                                setSearchParams(prev => { prev.set('dashboardIdx', dropIdx.toString()); return prev; }, { replace: true });
                                                            }
                                                        }}
                                                        onDragOver={(e) => {
                                                            if (edit.mode === 'edit' && canReorderDashboardTabs) {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                    >
                                                        {details.webdashboard?.map((wd, idx) => {
  const canView = checkTabPermission(userRole, wd.access_role);
  if (!canView) return null;

  const isActive = idx === currentDashboard;
  const isPopupOpen = editingTabRoleData?.index === idx;

  return (
    <Tooltip
      key={wd.id || idx}
      title={edit.mode === 'edit' && canReorderDashboardTabs ? t.dragchangeorder : ''}
      disableInteractive
      PopperProps={{ disablePortal: true }}
    >
      <div
        className={`dashboard-tab ${isActive ? 'active' : ''}`}
        draggable={false}
        data-original-index={idx}
        style={{
          textAlign: 'center',
          fontSize: 'calc(var(--ui-font-size) * var(--font-size-multiplier))',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '5px',
        }}
        onClick={() => {
          if (isPopupOpen) return;
          if (idx === currentDashboard) {
            setEditingTabRoleData(null);
            return;
          }

          setCurrentDashboard(idx);
          setSearchParams(
            (prev) => {
              prev.set('dashboardIdx', idx.toString());
              return prev;
            },
            { replace: true }
          );
          setEditingTabRoleData(null);
        }}
      >
        <span style={{ pointerEvents: 'none' }}>{wd.name}</span>

        {edit.mode === 'edit' && canReorderDashboardTabs && (
          <span
            className="dashboard-tab-drag-handle"
            draggable
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onDragStart={(e) => {
              e.stopPropagation();
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', idx.toString());
            }}
            aria-label={t.dragchangeorder || 'Drag to reorder tab'}
            title={t.dragchangeorder || 'Drag to reorder tab'}
            role="button"
          >
            ::
          </span>
        )}

        {edit.mode === 'edit' && (
          <button
            type="button"
            className="settings-btn dashboard-tab-settings-btn"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              editTriggerRef.current = e.currentTarget;
              const rect = e.currentTarget.getBoundingClientRect();
              setEditPopupAnchor(getTabPopupPosition(rect, { width: 320, estimatedHeight: 560 }));

              setEditingTabRoleData((prev) => {
                if (prev?.index === idx) return null;
                return {
                  index: idx,
                  name: wd?.name || '',
                  roles: Array.isArray(wd.access_role) ? [...wd.access_role] : [],
                  layoutMode: normalizeWebDashboardLayoutMode(wd?.layoutMode),
                };
              });
            }}
            style={{
              all: 'unset',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'var(--primary)',
              padding: '2px',
              borderRadius: '6px',
            }}
            aria-label={t.editDashboardRoles || 'Edit dashboard roles'}
          >
            <SettingsIcon
              style={{
                fontSize:
                  'calc(var(--ui-font-size) * var(--font-size-multiplier) * 0.8)',
              }}
            />
          </button>
        )}
      </div>
    </Tooltip>
  );
})}


                                                        <div className='dashboard-tab-border'></div>
                                                        {edit.mode === 'edit' ? (
                                                            <Tooltip title={t.dashboardAddTab}>
                                                                <div style={{ position: 'relative' }}>
                                                                    <span
                                                                        className="details-control-btn add-dashboard-trigger"
                                                                        style={{
                                                                            border: '1px dashed var(--add-tab-border)',
                                                                            marginBottom: '4px',
                                                                            fontSize: 'calc(var(--large) * var(--font-size-multiplier))'
                                                                        }}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();

                                                                            addTriggerRef.current = e.currentTarget;
                                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                                            setAddPopupAnchor(getTabPopupPosition(rect, { width: 340, estimatedHeight: 640 }));

                                                                            setAddDashboard(prev => ({
                                                                                ...prev,
                                                                                adding: true
                                                                            }));
                                                                        }}
                                                                    >
                                                                        +
                                                                    </span>
                                                                </div>
                                                            </Tooltip>
                                                        ) : null}
                                                    </div>

                                                        {edit.mode === 'edit' ? (
                                                            <div className='template-fixed-width-toggle'>
                                                                <span className='template-fixed-width-toggle-label'>
                                                                    {webDashboardT.multiSelectWidgets || 'Multi Select'}
                                                                </span>
                                                                <CustomSwitch
                                                                    checked={isMultiSelectEnabled}
                                                                    onChange={handleMultiSelectDashboardToggle}
                                                                    disabled={!isMultiSelectToggleReady}
                                                                />
                                                                <span className='template-fixed-width-toggle-label'>
                                                                    {webDashboardT.fixedWidthDashboard || 'Fixed Width Dashboard'}
                                                                </span>
                                                            <CustomSwitch
                                                                checked={isCurrentDashboardFixedWidth}
                                                                onChange={handleFixedWidthDashboardToggle}
                                                                disabled={!isFixedWidthToggleReady}
                                                            />
                                                        </div>
                                                    ) : null}
                                                </div>
                                                <div ref={dashboardViewportRef} className='td-dashboard-viewport'>
                                                    <WebDashboard mode={edit.mode} datastreams={details.datastreams?.filter(ds => ds)}
                                                        setDatastreams={(newDatastreams) => setDetails(prev => ({
                                                            ...prev,
                                                            datastreams: newDatastreams
                                                        }))} maxWidth='100%' maxHeight={dashboardMaxHeight}
                                                        externalFixedWidthControl={edit.mode === 'edit'}
                                                        onFixedWidthModeChange={handleFixedWidthStateChange}
                                                        onFixedWidthToggleHandlerReady={registerFixedWidthToggleHandler}
                                                        externalMultiSelectControl={edit.mode === 'edit'}
                                                        onMultiSelectStateChange={handleMultiSelectStateChange}
                                                        onMultiSelectToggleHandlerReady={registerMultiSelectToggleHandler}
                                                        dashboard={details.webdashboard[currentDashboard]}
                                                        setDashboard={(webDashboard) => {
                                                            setDetails(prevDetails => {
                                                                const updatedWebdashboards = [...(prevDetails.webdashboard || [])]; 
                                                                if (updatedWebdashboards[currentDashboard]) {
                                                                    updatedWebdashboards[currentDashboard] = webDashboard;
                                                                }
                                                                return {
                                                                    ...prevDetails,
                                                                    webdashboard: updatedWebdashboards
                                                                };
                                                            });
                                                        }} />
                                                </div>
                                            </div> :
                                            tabs[currentTab].name === t.tabs.eventsnotifications
                                                ? <TemplateEventsTab
                                                    details={details}
                                                    setAddDatastream={setAddDatastream}
                                                    edit={edit}
                                                    canEditEvent={canEditEvent}
                                                    t={t}
                                                    eventsFields={eventsFields}
                                                    pagingoptions={pagingoptions}
                                                    eventRowClick={eventRowClick}
                                                    selectedRows={selectedEventRows}
                                                    setSelectedRows={setSelectedEventRows}
                                                    selectActions={selectActions}
                                                    size={size}
                                                />
                                                : (tabs[currentTab].name === t.tabs.home ?
                                                    <TemplateHomeTab
                        details={details}
                        setDetails={setDetails}
                        edit={edit}
                        setEdit={setEdit}
                        canEditTemplate={canEditTemplate}
                        canAddTemplate={canAddTemplate}
                        canProvisionDevices={canProvisionDevices}
                        canDeleteTemplate={canDeleteTemplate}
                        canDownloadReports={canDownloadReports}
                        settings={settings}
                        setSettings={setSettings}
                        previousIcon={previousIcon}
                        setPreviousIcon={setPreviousIcon}
                        downloadReportState={downloadReportState}
                        setDownloadReportState={setDownloadReportState}
                        t={t}
                        isRtl={isRtl}
                        navigate={navigate}
                        updateTemplate={updateTemplate}
                        handleCancelEdit={handleCancelEdit}
                        onOpenAddDevice={() => setAddDevice(true)}
                    />
                                                    : <div style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>{tabs[currentTab].name} {t.tabLabel}</div>
                                                ))}
                                </div>
                            </div>
                        </div>
                    </div > </Fragment> : <div style={{
                        width: '100%', height: '100%', display: "flex",
                        alignItems: "center", justifyContent: "center"
                    }}>{templateTranslations[lang].noData}</div>
        : <div>{templateTranslations[lang].noPermission}</div>);
};
